import { branchDate } from '@lensmira/domain'
import type {
  JobTicket,
  PrescriptionCopy,
  ChoiceFinding,
  PriceExplanation,
  RoundingMode,
  WorkQueueGroup,
} from '@lensmira/domain'

import { parseMinorUnits, type WireMoney } from '../money.js'
import { parseClaimCapture, type ClaimCaptureData } from './claim-capture.js'
import type {
  PanelOption,
  PanelStatementsData,
  StatementTotalsData,
} from '../screens/PanelStatements.js'
import type { AgedData } from '../screens/PanelRemittance.js'
import type { CoverageRowData, PanelDetailData, PanelPlanData } from '../screens/PanelAccounts.js'
import { mapQuotaList, type MiraQuota } from '../mira/wire.js'
import type { CashSessionSummary } from '../screens/CashSession.js'
import type { CustomerDetailData } from '../screens/CustomerDetail.js'
import type { HoldView } from '../screens/StockHolds.js'
import type { CustomerCandidate } from '../screens/NewCustomer.js'
import type { ErasurePlanAnswer } from '../screens/CustomerErasure.js'
import type { FrameOption } from '../screens/FrameChoice.js'
import type { ClosedPeriodData } from '../screens/PeriodClose.js'
import type { CorrectionOffersData, MadeCorrectionData } from '../screens/Corrections.js'
import type { StaffRow } from '../screens/ShopSetup.js'
import type { TerminalRow } from '../screens/Terminals.js'
import type { QuoteData } from '../screens/Quote.js'
import type { ReceiptData } from '../screens/Receipt.js'
import type { LensSelectionData } from '../screens/LensChoice.js'
import type { PriceImpactData } from '../screens/PriceImpact.js'
import type { ReportListing, ReportRun } from '../screens/Reports.js'
import type { NotificationData } from '../screens/Notifications.js'
import type { RecallEntryData } from '../screens/Recalls.js'
import type { RepairData } from '../screens/Repairs.js'
import type { MessageData } from '../screens/Outbox.js'
import type { MessageTemplateOption } from '../screens/SendMessage.js'
import type { PromotionData } from '../screens/Promotions.js'
import type {
  CommissionSchemeVersionData,
  SchemeRuleData,
  SchemeTierData,
} from '../screens/CommissionSchemes.js'
import type {
  StockMovementData,
  StockPositionData,
  TrialExpiryData,
  TrialExpiryRow,
} from '../screens/Stock.js'
import type { PurchaseOrderData, StockLocationOption } from '../screens/Purchasing.js'
import type { LensRow, PriceBookRow } from '../screens/PriceBooks.js'
import type { CompatibilityRow, TreatmentRow } from '../screens/LensTreatments.js'
import type { CatalogueFrameRow, FrameImageRow } from '../screens/FrameImages.js'
import type { TransferData } from '../screens/Transfers.js'
import type { ShipmentData, ShipmentOrderData } from '../screens/Shipments.js'
import type { PurchaseOrderDetailData } from '../screens/SupplierInvoice.js'
import type { JobDetailData, RemakeLifeRow } from '../screens/JobDetail.js'
import type { OrderDetailData } from '../screens/OrderDetail.js'
import type { OrderSummary } from '../screens/OrderList.js'
import type { LoanFrameData } from '../screens/LoanFrames.js'
import type { RefractionData } from '../screens/Refractions.js'
import type { BranchGroupRow } from '../screens/CustomerSharing.js'
import type { RecallRuleData } from '../screens/RecallRules.js'
import type { ClosureRow, OverrideRow } from '../screens/AppointmentCalendar.js'
import type { ScheduleRow } from '../screens/AppointmentHours.js'
import type { ExpenseView } from '../screens/Expenses.js'
import type { PoliciesRow } from '../screens/TenantPolicies.js'
import type {
  AppointmentResourceRow,
  AppointmentTypeRow,
  VisitDatesRow,
} from '../screens/AppointmentOffers.js'
import type { SupplierPriceData } from '../screens/SupplierPrices.js'
import type { ClProductList } from '../screens/ClCatalogue.js'
import type { AppointmentRow, BookableOptions, OpenSlotsData, SlotQuery } from '../screens/Diary.js'

/**
 * Reads, and only reads.
 *
 * **`OFF-001a` says every write goes through the queue, and says nothing about reads, deliberately.**
 * A queued write is durable because a half-finished sale must survive the shop losing power; a read
 * has nothing to preserve and queueing one would mean a screen waiting on a retry loop to find out
 * what an order costs. So writes go to `useWriteQueue` and reads come through here, and the split is
 * stated in one place rather than re-argued per screen.
 *
 * Every function takes the token explicitly rather than reading a module-level variable. `CLAUDE.md`
 * is about the server when it says tenant context is never a global, and the reason applies harder
 * here: on the client a global survives a route change and a user switching shops, where on the
 * server it would at least die with the request.
 */

export interface ReadsConfig {
  readonly baseUrl: string
  readonly tenantId: string
  readonly token: string
  readonly doFetch: typeof globalThis.fetch
}

export class ReadFailed extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'ReadFailed'
  }
}

export interface TerminalContext {
  readonly terminalId: string
  readonly seriesCode: string
  readonly fiscalYear: number
  readonly numbersRemaining: number
}

export interface BranchContext {
  readonly id: string
  readonly name: string
  readonly code: string
  readonly timezone: string
  readonly currency: string
  /** §13's country pack. The Setup screen offers a second branch the same one. */
  readonly countryCode: string
  /** Null when the branch has no series set. A confirm is then impossible, and says so. */
  readonly invoiceSeriesTemplate: string | null
  readonly terminals: readonly TerminalContext[]
}

export interface SessionContext {
  readonly staff: {
    readonly id: string
    readonly displayName: string
    readonly role: string
    readonly permissions: readonly string[]
    /**
     * This person's own sign-in address and whether they have an authenticator, so the settings
     * screen can offer to set one up. `TEN-005`, `decisions/0014`.
     *
     * Optional because an older server does not send either key, in which case the panel is not
     * offered rather than offered with a blank address.
     */
    readonly loginIdentifier?: string
    readonly totpEnrolled?: boolean
    /** `ROLE-001a`: where this person may see cost price, which is where they may grant it. */
    readonly costVisibleBranchIds?: readonly string[]
    /**
     * `LOC-002`: this person's own language, a BCP-47 tag. `staff.language` since migration 0034,
     * and null until somebody sets it, which is what makes the shop's setting reachable.
     *
     * Optional rather than `string | null` because an older server does not send the key at all,
     * and `resolveLocale` treats absent and null alike. It is the only place the client reads a
     * person's language: nothing else may invent a second source.
     */
    readonly language?: string | null
  }
  readonly branches: readonly BranchContext[]
  readonly settings: {
    /**
     * `LOC-002`: the shop's default language, `tenant_settings.language` since migration 0034.
     * Null is "the shop has configured nothing", not English: `resolveLocale` falls back to English
     * here in the client, so the two are distinguishable.
     */
    readonly language?: string | null
    readonly customerVisibility: string
    readonly returnWindowDays: number | null
    readonly rxStalenessMonths: number | null
    readonly rxExpiryMonths: number | null
    readonly anisometropiaDioptres: string | null
    readonly layawayForfeitPercent: string | null
    /** `PRC-013`. Null means the shop has set no rounding direction and cannot raise an order. */
    readonly pricingRoundingMode: RoundingMode | null
    /** `TEN-005`, `decisions/0014`: whether this shop asks privileged roles to enrol a TOTP. */
    readonly requireTotp: boolean
    /** `MR-06`. Always null: nothing stores it. The counter rounds nothing until it does. */
    readonly cashRounding: null
  }
  /**
   * `TEN-006`: digits in a PIN, or null when the deployment has PIN sign-in off. Optional because an
   * older server does not send it, and absent reads as off.
   */
  readonly pinLength?: number | null
}

const request = async <T>(config: ReadsConfig, path: string): Promise<T> => {
  const response = await config.doFetch(`${config.baseUrl}${path}`, {
    headers: {
      authorization: `Bearer ${config.token}`,
      'X-Lensmira-Tenant': config.tenantId,
    },
  })

  if (!response.ok) {
    // The body is the API's own `{ code, message }`, so the screen can say what went wrong rather
    // than "something went wrong". A body that does not parse is not allowed to mask the status.
    const body = (await response.json().catch(() => undefined)) as
      { code?: unknown; message?: unknown } | undefined
    throw new ReadFailed(
      response.status,
      typeof body?.code === 'string' ? body.code : 'READ_FAILED',
      typeof body?.message === 'string' ? body.message : `Request failed (${response.status}).`,
    )
  }

  return (await response.json()) as T
}

/**
 * The two writes in this file, and the only two in the counter that do not go through the queue.
 * §6.4 `CAT-004`, `decisions/0019`.
 *
 * **This is a deliberate exception and the only one.** `OFF-001a` says every write from the UI goes
 * through the write queue, and `CLAUDE.md` lists it among the invariants: "no direct API calls from
 * components. This is what makes full offline a contained change later instead of a rewrite." A
 * frame photograph is two to six megabytes and the queue is IndexedDB on a cheap Android tablet, so
 * a shop photographing forty frames at three angles would hold half a gigabyte of JPEG in browser
 * storage to keep a promise the queue was not made for.
 *
 * They live in `reads.ts` rather than in a component, which is the half of the invariant that still
 * holds: a screen calls a function from this module and does not know about `fetch`. The cost is
 * that an upload which fails is one the shopkeeper repeats, and `FrameImages` says so rather than
 * pretending a queue is holding it.
 *
 * Nothing else should use these. A second caller is a sign the queue is being worked around rather
 * than a megabyte problem being solved.
 */
const writeDirect = async (
  config: ReadsConfig,
  method: 'POST' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<void> => {
  const response = await config.doFetch(`${config.baseUrl}${path}`, {
    method,
    headers: {
      authorization: `Bearer ${config.token}`,
      'X-Lensmira-Tenant': config.tenantId,
      'content-type': 'application/json',
      // `ARC-006`: every mutating endpoint takes one, queue or no queue. A double-tapped upload
      // must not store the bytes twice under two keys and leave an orphan.
      'idempotency-key': `img-${globalThis.crypto.randomUUID()}`,
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })
  if (!response.ok) {
    const failure = (await response.json().catch(() => undefined)) as
      { code?: unknown; message?: unknown } | undefined
    throw new ReadFailed(
      response.status,
      typeof failure?.code === 'string' ? failure.code : 'WRITE_FAILED',
      typeof failure?.message === 'string'
        ? failure.message
        : `Request failed (${response.status}).`,
    )
  }
}

export const postDirect = async (
  config: ReadsConfig,
  path: string,
  body: unknown,
): Promise<void> => {
  await writeDirect(config, 'POST', path, body)
}

export const deleteDirect = async (config: ReadsConfig, path: string): Promise<void> => {
  await writeDirect(config, 'DELETE', path)
}

/**
 * The same request, returning bytes. §6.4 `CAT-004`.
 *
 * A separate helper rather than a flag on `request`, because the error path is identical and the
 * success path is the only difference: a `json()` on an image is a parse failure dressed as a read
 * failure. The refusal body is still the API's own `{ code, message }`, so a 403 on a picture says
 * which permission, exactly as every other read does.
 */
const requestBlob = async (config: ReadsConfig, path: string): Promise<Blob> => {
  const response = await config.doFetch(`${config.baseUrl}${path}`, {
    headers: {
      authorization: `Bearer ${config.token}`,
      'X-Lensmira-Tenant': config.tenantId,
    },
  })
  if (!response.ok) {
    const body = (await response.json().catch(() => undefined)) as
      { code?: unknown; message?: unknown } | undefined
    throw new ReadFailed(
      response.status,
      typeof body?.code === 'string' ? body.code : 'READ_FAILED',
      typeof body?.message === 'string' ? body.message : `Request failed (${response.status}).`,
    )
  }
  return await response.blob()
}

/**
 * The list a read expects, still typed. `ARC-020`, and the reason the mappers exist at all.
 *
 * **`Array.isArray` narrows `readonly T[]` to `any[]`.** Five list reads guarded their array with
 * it and then mapped the rows, so inside every one of those mappers the row was `any`: a field the
 * server does not send, or a field misspelled here, compiled and arrived as `undefined` at the
 * till. That is the exact failure the mapper layer was written to stop, and it had stopped
 * stopping it. Nothing reported it, because `noImplicitAny` only speaks up about a parameter, and
 * the first nested `.map` in these reads is what finally produced one.
 *
 * The declared return type is what restores the check: `any[]` is assignable to it, and every
 * caller downstream is typed again.
 */
const listOf = <T>(value: readonly T[] | undefined, what: string): readonly T[] => {
  if (!Array.isArray(value)) {
    throw new ReadFailed(
      500,
      'READ_CONTRACT',
      `The server did not send a list of ${what}. This is a version mismatch, not your fault.`,
    )
  }
  return value
}

export const readSessionContext = (config: ReadsConfig): Promise<SessionContext> =>
  request<SessionContext>(config, '/auth/context')

/**
 * The wire shape of `GET /orders`.
 *
 * Fourth instance of the same mismatch, and the one that proves the pattern is not a coincidence:
 * every screen written before its endpoint guessed, and every guess typechecked. Here the server
 * sends `total` and `outstanding` as `{minor, currency}` and the screen wanted `totalMinor`,
 * `outstandingMinor` and a separate `currency`, so `parseMinorUnits(undefined)` threw
 * `MoneyInputError` and React unmounted the list on the first real order.
 *
 * `branchName` is supplied by the caller rather than the row: `branchId` is a required parameter, so
 * the branch is what was asked for, not something the answer carries. `staffName` has no source yet.
 */
/**
 * A job as the order **detail** sends it, which is not what the list sends.
 *
 * The detail carries `job_number` and the list does not, and the number is the whole point of the
 * field: a panel that has just raised a job has to be able to say which job, and an id is not
 * something a shopkeeper reads out to a lab.
 */
interface WireOrderDetailJob {
  readonly id: string
  readonly jobNumber: string
  readonly status: string
  readonly promisedAt: string | null
}

interface WireOrderJob {
  readonly id: string
  readonly status: string
  readonly promisedAt: string | null
}

interface WireOrderSummary {
  readonly id: string
  readonly orderNumber: string | null
  readonly status: string
  readonly customerName: string | null
  readonly total: WireMoney
  readonly outstanding: WireMoney
  readonly refunded: WireMoney
  readonly placedAt: string | null
  readonly jobs: readonly WireOrderJob[]
}

const toOrderSummary = (
  wire: WireOrderSummary,
  branchName: string | undefined,
  timeZone: string,
): OrderSummary => ({
  id: wire.id,
  // A draft has no invoice number: `TAX-002` takes one at confirm and not before.
  number: wire.orderNumber ?? null,
  // `SAL-001`'s anonymous quick sale has no customer at all, which is a real state and not a gap.
  customerName: wire.customerName ?? 'No customer',
  status: wire.status as OrderSummary['status'],
  currency: need(wire.total, 'total').currency,
  totalMinor: wire.total.minor,
  outstandingMinor: need(wire.outstanding, 'outstanding').minor,
  refundedMinor: need(wire.refunded, 'refunded').minor,
  ...(branchName === undefined ? {} : { branchName }),
  jobs: wire.jobs.map((job) => ({
    id: job.id,
    status: job.status as OrderSummary['jobs'][number]['status'],
    /**
     * A calendar date, not an instant, **in the branch's timezone**. Appendix J; `LOC-004`.
     *
     * The screen compares it as a string against today to decide overdue, so an ISO timestamp
     * would compare wrongly for most of every day. It was sliced off the UTC instant, which is a
     * different kind of wrong and a quieter one: Pakistan is UTC+5, so a job promised before 05:00
     * local belongs to the previous UTC day. The order list showed its date a day early and
     * flagged it overdue in red on the morning it was due, while the customer's own record showed
     * the right date, because `customer-detail-read.service.ts` already casts at the branch zone
     * in SQL.
     *
     * `branchDate` rather than a display formatter: this value is compared, not printed, and
     * `formatDay` renders a bare calendar date at UTC, which would leave the string unchanged.
     */
    ...(job.promisedAt === null
      ? {}
      : { promisedOn: branchDate(Date.parse(job.promisedAt), timeZone) }),
  })),
})

/**
 * **Two adjacent `string` parameters, and the compiler cannot tell them apart.**
 *
 * Adding `timeZone` beside `branchName` left the one call site passing `branch.name` as the zone,
 * and `tsc` was happy: `branchDate` would then have thrown on `Anarkali` at runtime, on the
 * counter's landing screen. Caught by reading the call site rather than by the typecheck that had
 * just passed. If a third string is ever needed here, give this an options object instead.
 */
export const readOrders = async (
  config: ReadsConfig,
  branchId: string,
  timeZone: string,
  branchName?: string,
): Promise<readonly OrderSummary[]> => {
  const body = await request<{ orders: readonly WireOrderSummary[] }>(
    config,
    `/orders?branchId=${encodeURIComponent(branchId)}`,
  )
  return body.orders.map((order) => toOrderSummary(order, branchName, timeZone))
}

/**
 * The wire shape of `GET /orders/:orderId`, written from what the endpoint actually returns.
 *
 * It is not the shape `OrderDetail.tsx` renders, and the gap is the point. That screen was written
 * before the endpoint existed, from a "best-effort typed guess at the wire shape" its author said so
 * in as many words, and nothing reconciled the guess afterwards: `request<T>` casts parsed JSON to
 * whatever interface it is handed, so the compiler agreed with a shape the server has never sent.
 * Three of the six fields the screen renders are absent from the response, and every money field on
 * a line arrives under a different name as `{minor, currency}` rather than a bare string.
 *
 * So the conversion lives here rather than in the screen. Reasons, in order:
 *
 * 1. **One place knows the wire.** A screen that parses the API is a screen that has to be re-read
 *    every time the API moves. Two screens doing it differently is how the two disagree.
 * 2. **It can refuse.** The mapper checks the fields it needs are present and throws a `ReadFailed`
 *    naming the missing one, so a contract change is a message rather than a white screen. That is
 *    the whole lesson of the cash drawer crash: a required field the server never sent typechecked
 *    perfectly and took the screen down at runtime.
 * 3. **Money stays exact.** Minor units cross as strings and are summed as `bigint`. Nothing here
 *    rounds: Appendix D `MR-04` allows rounding at three points and none of them is a screen.
 */
interface WireOrderDetailLine {
  readonly id: string
  readonly description: string
  readonly quantity: number
  readonly saleType: string
  readonly returnsPosition: string
  readonly priced: WireMoney
  readonly lineDiscount: WireMoney
  readonly promotion: WireMoney
  readonly orderDiscountShare: WireMoney
  readonly tax: WireMoney
  readonly gross: WireMoney
  readonly explanation: readonly PriceExplanation[]
}

interface WireOrderDetailPayment {
  readonly id: string
  readonly method: string
  readonly amount: WireMoney
  readonly takenAt: string
  readonly takenByStaffId: string | null
  readonly takenByName: string | null
}

interface WireOrderDetail {
  readonly id: string
  readonly status: string
  readonly version: number
  readonly number: string | null
  readonly total: WireMoney
  readonly outstanding: WireMoney
  /** What a refund gave back, beside the invoice rather than netted into it. `POS-005`. */
  readonly refunded: WireMoney
  readonly customerId: string | null
  /** Appendix E's sale type for the whole order. Optional: an older server does not send it. */
  readonly saleType?: string
  readonly prescriptionId: string | null
  readonly jobs: readonly WireOrderDetailJob[]
  readonly lines: readonly WireOrderDetailLine[]
  readonly payments: readonly WireOrderDetailPayment[]
}

/**
 * A field this mapper cannot do without, as text or as a number.
 *
 * **The doctrine above is already written and `readSuppliers` broke it.** It read
 * `version: Number(wire['version'] ?? 1)`, so a server that stopped sending a version produced a
 * row carrying 1, the screen sent that as `expectedVersion`, and the write came back 409
 * `VERSION_CONFLICT`. A 409 is a refusal, a refusal blocks the head of the write queue, and the
 * queue holds everything behind it including taking payment. The shopkeeper's only clue would have
 * been a till that stopped.
 *
 * It also read `id: String(wire['id'])`, and `String(undefined)` is the four characters
 * `undefined`, so a missing id does not crash: it renders, and is sent back to the server as a
 * record that does not exist. That is the exact failure the comment on the mapper layer says this
 * layer exists to stop, which is why the fix is a helper the other mappers can take rather than one
 * patched call site.
 *
 * Found twice independently, by two agents surveying two different screens that would have copied
 * it.
 */
const wireText = (wire: Record<string, unknown>, key: string, what: string): string => {
  const value = wire[key]
  if (value === null || value === undefined || value === '') {
    throw new ReadFailed(
      500,
      'READ_CONTRACT',
      `The server sent ${what} with no ${key}. This is a version mismatch, not your fault.`,
    )
  }
  return String(value)
}

const wireNumber = (wire: Record<string, unknown>, key: string, what: string): number => {
  const value = Number(wire[key])
  if (wire[key] === null || wire[key] === undefined || !Number.isFinite(value)) {
    throw new ReadFailed(
      500,
      'READ_CONTRACT',
      `The server sent ${what} with no usable ${key}. This is a version mismatch, not your fault.`,
    )
  }
  return value
}

const need = <T>(value: T | null | undefined, field: string): T => {
  if (value === null || value === undefined) {
    throw new ReadFailed(
      500,
      'READ_CONTRACT',
      `The server's order detail is missing ${field}. This is a version mismatch, not your fault.`,
    )
  }
  return value
}

/**
 * One line's total reduction: its own discount, plus any promotion, plus its apportioned share of
 * an order-level discount.
 *
 * The server keeps the three apart because they are three different decisions and `MR-05` requires
 * the order-level one to be apportioned back to lines so per-line margin stays truthful. The screen
 * shows one "Discount" column, so they are added here rather than one of them being picked and the
 * other two quietly dropped, which would understate the discount on exactly the orders where it
 * matters most.
 */
const totalLineDiscount = (line: WireOrderDetailLine): bigint =>
  BigInt(line.lineDiscount.minor) +
  BigInt(line.promotion.minor) +
  BigInt(line.orderDiscountShare.minor)

const toOrderDetailData = (wire: WireOrderDetail): OrderDetailData => {
  const currency = need(wire.total, 'total').currency
  return {
    id: wire.id,
    status: wire.status,
    version: wire.version,
    // A draft has no invoice number: `TAX-002` takes one at confirm and not before. Shown as a dash
    // rather than an empty gap, so the row does not read as a rendering fault.
    number: wire.number ?? null,
    currency,
    // Passed through as null rather than dropped. A sale with no customer is a walk-in, and a
    // screen that needs one has to be able to tell that from a field the mapper forgot.
    customerId: wire.customerId ?? null,
    prescriptionId: wire.prescriptionId ?? null,
    ...(typeof wire.saleType === 'string' ? { saleType: wire.saleType } : {}),
    // Checked rather than defaulted to `[]`: an absent list is a version mismatch and reads
    // exactly like a sale with nothing at the lab, which is the one thing it must not read as.
    jobs: listOf(wire.jobs, "the sale's jobs").map((job) => {
      const row = asRecord(job, 'a job on the sale')
      return {
        id: String(row['id']),
        jobNumber: String(row['jobNumber']),
        // Unnarrowed per `ARC-020`, like every other status on this read.
        status: String(row['status']),
        /*
         * **No `promisedOn` here, deliberately.** Appendix J.
         *
         * This mapper sliced one off the UTC instant, which is the defect fixed in
         * `toOrderSummary` above: in Pakistan a job promised before 05:00 local belongs to the
         * previous UTC day. The difference is that **nothing reads this one**. `OrderDetail.tsx`
         * declared the field and never rendered it, so the choice was to thread a branch timezone
         * through `readOrder` and its nine call sites to compute a value nobody looks at, or to
         * stop computing it. A value that is never read cannot be worth being wrong.
         *
         * The order list's copy is the one that matters and is computed with `branchDate`. If a
         * promised date is ever wanted on this screen, take it the same way.
         */
      }
    }),
    totalMinor: wire.total.minor,
    outstandingMinor: need(wire.outstanding, 'outstanding').minor,
    refundedMinor: need(wire.refunded, 'refunded').minor,
    lines: wire.lines.map((line) => ({
      id: line.id,
      description: line.description,
      quantity: line.quantity,
      saleType: line.saleType as OrderDetailData['lines'][number]['saleType'],
      returnsPosition: line.returnsPosition as OrderDetailData['lines'][number]['returnsPosition'],
      pricedMinor: line.priced.minor,
      discountMinor: totalLineDiscount(line).toString(),
      taxMinor: line.tax.minor,
      totalMinor: line.gross.minor,
      ...(line.explanation.length === 0 ? {} : { priceExplanation: line.explanation }),
    })),
    payments: wire.payments.map((payment) => ({
      id: payment.id,
      method: payment.method as OrderDetailData['payments'][number]['method'],
      amountMinor: payment.amount.minor,
      takenAt: payment.takenAt,
      // `COR-001`'s fourth combination needs both: the name to show and the id to correct against.
      // Mapped rather than cast, like every other field here, because the server not sending one
      // is a sentence and not a white screen.
      takenByStaffId: payment.takenByStaffId ?? null,
      takenByName: payment.takenByName ?? null,
    })),
  }
}

export const readOrder = async (config: ReadsConfig, orderId: string): Promise<OrderDetailData> =>
  toOrderDetailData(
    await request<WireOrderDetail>(config, `/orders/${encodeURIComponent(orderId)}`),
  )

/**
 * The wire shape of `GET /jobs/:jobId`, written from what the endpoint actually returns.
 *
 * Third instance of the same defect, and the worst of the three. `JobDetail.tsx` types the job as
 * having `od` and `os`, and does `const eye = job[side]` then reads `eye.sphere`. The server sends
 * one `eyeSnapshot` array with an `eye` discriminator, so on real data that is
 * `undefined.sphere` and the screen **throws during render**: not mistyped, broken. The cash drawer
 * crash was the same shape, the order detail was the same shape, and all three typechecked because
 * `request<T>` casts.
 *
 * Two more differences, both quieter and both real:
 *
 * - The screen types `status` as the `JobStatus` union. The server types it `string`, deliberately:
 *   `ARC-020` means a status this build has never heard of can arrive from a newer schema during a
 *   migration fan-out, and a union would make that a type lie rather than a rendering problem. The
 *   mapper passes it through and the screen's label lookup falls back.
 * - `latestQualityCheck` does not exist on the wire. The server sends `qualityChecks`, newest first,
 *   so the latest is element zero. Today the QC panel simply never renders, which on a screen whose
 *   whole point is `JOB-005a` is the most expensive silent failure of the three.
 */
interface WireEyeSnapshot {
  readonly eye: string
  readonly sphere: number
  readonly cylinder: number
  readonly axis?: number
  readonly add?: number
  readonly monocularPdMm?: number
  readonly segmentHeightMm?: number
}

interface WireQualityCheck {
  readonly checkedByStaffId: string
  readonly checkedAt: string
  readonly passed: boolean
  readonly failureReason?: string
}

interface WireJobDetail {
  readonly id: string
  readonly jobNumber: string
  readonly status: string
  readonly heldFromStatus?: string
  /**
   * Appendix C.1's status of the order this job fulfils. §6.9 `POS-005`.
   *
   * `job-detail-read.service.ts` has sent this since it was written, and this mapper dropped it.
   * A job's own row is untouched by a void, so a job whose sale was cancelled still reads `ready`,
   * and the collect form gated on that alone: the screen offered to hand over glasses for a sale
   * that no longer exists, and `jobs.collect` refused the write with `ORDER_NOT_COLLECTABLE`.
   */
  readonly orderStatus: string
  readonly currency: string
  readonly eyeSnapshot: readonly WireEyeSnapshot[]
  readonly qualityChecks: readonly WireQualityCheck[]
  /**
   * `RMK-008`: the remakes raised against this job. Absent on an older server, which is why the
   * screen draws nothing rather than an empty block.
   *
   * `supplierCreditExpected` is `ROLE-001a`-gated and arrives absent without the per-branch cost
   * grant, which is what decides whether the credit button is offered at all.
   */
  readonly remakes?: readonly {
    readonly id: string
    readonly status: string
    readonly supplierCreditExpected?: WireMoney
    readonly creditReceivedAt?: string
    readonly resolvedAt?: string
  }[]
}

/**
 * One eye of the snapshot, or nothing, because **one eye is a legitimate job**.
 *
 * This used to throw `READ_CONTRACT` for an absent side, which made the screen unable to display a
 * single lens replacement at all: `sale-type.ts` defines that type with `requiresEye: true` and
 * `singleLensPricing: true`, and §12's edge case 114 is the whole point of it, so a job with one
 * snapshot is the ordinary shape of a real sale rather than a corrupt record.
 *
 * `toJobDetailData` still refuses when **both** are missing. That one is a contract breach: a
 * spectacle job made to no prescription at all cannot be shown, and `JOB-002` says the snapshot is
 * frozen at order time, so an empty one means the write path lost it.
 */
const eyeOrAbsent = (
  snapshots: readonly WireEyeSnapshot[],
  side: 'od' | 'os',
): WireEyeSnapshot | undefined => snapshots.find((snapshot) => snapshot.eye === side)

/**
 * `RMK-008`: one remake as the credit and close buttons need it.
 *
 * `creditExpected` is derived from `supplierCreditExpected` rather than carried: the screen needs
 * to know **whether** the lab owes a credit, not how much, and the amount is gated by
 * `ROLE-001a`'s per-branch cost grant. Left undefined when the amount is withheld, which is what
 * makes the screen offer no credit button: pressing it on a remake with no credit expected is a 409
 * that halts the write queue.
 */
const toRemakeLife = (wire: WireJobDetail): readonly RemakeLifeRow[] | undefined => {
  if (wire.remakes === undefined) return undefined
  return wire.remakes.map((row) => ({
    id: row.id,
    status: row.status,
    ...(row.supplierCreditExpected === undefined
      ? {}
      : { creditExpected: row.supplierCreditExpected.minor !== '0' }),
    ...(row.creditReceivedAt === undefined ? {} : { creditReceivedAt: row.creditReceivedAt }),
    ...(row.resolvedAt === undefined ? {} : { resolvedAt: row.resolvedAt }),
  }))
}

const toJobDetailData = (wire: WireJobDetail): JobDetailData => {
  const od = eyeOrAbsent(wire.eyeSnapshot, 'od')
  const os = eyeOrAbsent(wire.eyeSnapshot, 'os')
  if (od === undefined && os === undefined) {
    throw new ReadFailed(
      500,
      'READ_CONTRACT',
      'This job has no prescription recorded for either eye, so it cannot be shown. JOB-002 freezes the snapshot at order time, so this is a write that lost it rather than a job made without one.',
    )
  }
  const latest = wire.qualityChecks[0]

  return {
    id: wire.id,
    jobNumber: wire.jobNumber,
    // `ARC-020`: the server types this `string` on purpose. Cast at the boundary and let the
    // screen's label lookup fall back, rather than pretending a newer schema cannot exist.
    status: wire.status as JobDetailData['status'],
    // Copied as the server sends it, not reduced to a boolean here: whether a sale is still live
    // is `checkOrderTransition`'s question, and the screen asks it with the same function the
    // write path uses rather than keeping a second answer.
    orderStatus: wire.orderStatus,
    ...(wire.heldFromStatus === undefined
      ? {}
      : { heldFromStatus: wire.heldFromStatus as JobDetailData['status'] }),
    ...(() => {
      const remakes = toRemakeLife(wire)
      return remakes === undefined ? {} : { remakes }
    })(),
    currency: wire.currency,
    ...(od === undefined ? {} : { od }),
    ...(os === undefined ? {} : { os }),
    ...(latest === undefined ? {} : { latestQualityCheck: latest }),
  }
}

export const readJob = async (config: ReadsConfig, jobId: string): Promise<JobDetailData> =>
  toJobDetailData(await request<WireJobDetail>(config, `/jobs/${encodeURIComponent(jobId)}`))

/**
 * The drawer this terminal is on, or null when there is none.
 *
 * A 404 is not an error here: "no open session" is the ordinary state of a till before the shop
 * opens, and treating it as a failure would put an error banner over an empty counter every morning.
 * Every other status still throws.
 */
interface WireCashSession {
  readonly id: string
  readonly status: string
  readonly terminalId: string
  /** Withheld while a session is uncounted: `POS-004`'s blind count. */
  readonly openingFloat?: WireMoney
  /** How many pay-ins and pay-outs, never their amounts, for the same reason. */
  readonly movementCount?: number
  /** Present once somebody has counted. `cash-session-read.service.ts` has always sent it. */
  readonly countedByStaffId?: string
}

/**
 * The wire shape mapped, not cast.
 *
 * This was the last read in this file still handing a `request<T>` cast straight to a screen, and
 * `movementCount` is exactly the kind of field that gets lost that way: the server has answered with
 * it all along and the screen said "No pay-ins or pay-outs recorded yet" over a drawer that had one,
 * because nothing carried it across.
 */
const toCashSessionSummary = (wire: WireCashSession): CashSessionSummary => ({
  id: need(wire.id, 'the session id'),
  status: wire.status as CashSessionSummary['status'],
  terminalId: need(wire.terminalId, 'the terminal'),
  ...(wire.openingFloat === undefined ? {} : { openingFloat: wire.openingFloat }),
  ...(wire.movementCount === undefined ? {} : { movementCount: wire.movementCount }),
  // Present once a count exists, absent on an open session, which is the server withholding it
  // rather than a gap: nobody has counted yet. It is what lets the screen offer the approval to
  // the one person who can use it.
  ...(wire.countedByStaffId === undefined ? {} : { countedByStaffId: wire.countedByStaffId }),
})

export const readCurrentCashSession = async (
  config: ReadsConfig,
  branchId: string,
  terminalId: string,
): Promise<CashSessionSummary | null> => {
  try {
    const body = await request<{ session: WireCashSession | null }>(
      config,
      `/cash-sessions/current?branchId=${encodeURIComponent(branchId)}&terminalId=${encodeURIComponent(terminalId)}`,
    )
    return body.session === null ? null : toCashSessionSummary(body.session)
  } catch (error) {
    if (error instanceof ReadFailed && error.status === 404) return null
    throw error
  }
}

/**
 * `GET /work-queue`. §16.3's landing surface.
 *
 * No adapter: `WorkQueueGroup` and `WorkQueueItem` come from `packages/domain` and the endpoint
 * returns them unchanged, so both sides already name the same type rather than two guesses at it.
 * That is the shape every read should eventually have.
 */
/**
 * `GET /customers?q=`, for `NewCustomer`'s `CUS-003` check before a second record is filed.
 *
 * `CustomerSearch` calls the same endpoint through its own injected `doFetch`, which
 * `apps/counter/eslint.config.js` permits in that one file. This is the shell's path to it, so a
 * screen that is not that one gets Appendix K's matcher without a second direct fetch in the app.
 *
 * `asList` rather than a cast: a server that sent no `matches` would otherwise crash a mapper, and
 * problem 109 is four screens that guessed a wire shape, typechecked, and crashed in a browser.
 */
export const readCustomerMatches = async (
  config: ReadsConfig,
  query: string,
): Promise<readonly CustomerCandidate[]> => {
  const body = await request<{ matches?: unknown }>(
    config,
    `/customers?q=${encodeURIComponent(query)}`,
  )
  return asList(body.matches, 'the customer matches').map((row) => {
    const wire = row as Record<string, unknown>
    return {
      id: String(wire['id']),
      name: String(wire['name']),
      matchedOn: String(wire['matchedOn']) as CustomerCandidate['matchedOn'],
      // `GET /customers` carries no date of birth. The field exists because the create's own answer
      // does carry one (`CUS-003`'s "name+DOB fuzzy"), and one shape for both lists means the
      // screen renders a candidate the same way wherever it came from.
      dateOfBirth: null,
    }
  })
}

/**
 * `GET /staff`. §6.31 `ROLE-001`, `ROLE-005`.
 *
 * The read that makes offboarding reachable from a screen rather than from somebody who already
 * knows a staff id.
 *
 * `offboardedAt` is passed through as null when absent, because a screen asks "do they still work
 * here" and an absent field would read as yes by accident rather than on purpose.
 */
export const readStaff = async (config: ReadsConfig): Promise<readonly StaffRow[]> => {
  const body = await request<{ staff?: unknown }>(config, '/staff')
  return asList(body.staff, 'the people at this shop').map((row) => {
    const wire = row as Record<string, unknown>
    return {
      id: String(wire['id']),
      displayName: String(wire['displayName']),
      role: String(wire['role']),
      branchIds: asList(wire['branchIds'] ?? [], 'a branch list').map((id) => String(id)),
      costVisibleBranchIds: asList(wire['costVisibleBranchIds'] ?? [], 'a cost branch list').map(
        (id) => String(id),
      ),
      offboardedAt:
        wire['offboardedAt'] === undefined || wire['offboardedAt'] === null
          ? null
          : String(wire['offboardedAt']),
      version: Number(wire['version'] ?? 1),
      // `TEN-006`. Absent from an older server, which reads as no PIN rather than as a failure.
      pinUsername:
        wire['pinUsername'] === undefined || wire['pinUsername'] === null
          ? null
          : String(wire['pinUsername']),
    }
  })
}

/** One counter terminal, as the enrolment panel needs it. `TEN-006`. */
export const readTerminals = async (config: ReadsConfig): Promise<readonly TerminalRow[]> => {
  const body = await request<{ terminals?: unknown }>(config, '/terminals')
  return asList(body.terminals, 'the counter terminals').map((row) => {
    const wire = row as Record<string, unknown>
    return {
      id: String(wire['id']),
      branchId: String(wire['branchId']),
      name: String(wire['name']),
      enrolledAt: String(wire['enrolledAt']),
      revokedAt:
        wire['revokedAt'] === undefined || wire['revokedAt'] === null
          ? null
          : String(wire['revokedAt']),
      revokedReason:
        wire['revokedReason'] === undefined || wire['revokedReason'] === null
          ? null
          : String(wire['revokedReason']),
      version: Number(wire['version'] ?? 1),
    }
  })
}

/** One supplier, as the chooser and the maintenance panel need it. §6.19 `SUP-001`. */
export interface SupplierRow {
  readonly id: string
  readonly name: string
  readonly code: string
  readonly countryCode: string
  readonly currency: string
  /** Appendix E `supplier_channel`, or null when nobody has set one: `SUP-003` then refuses. */
  readonly transmissionChannel: string | null
  readonly leadTimeDays: number | null
  readonly phone: string | null
  readonly email: string | null
  readonly isActive: boolean
  /** `ARC-012`'s lock, so the list a change is chosen from is the one it is sent against. */
  readonly version: number
}

/**
 * `GET /suppliers`. §6.19 `SUP-001`; `later_todo.md` 53.
 *
 * The read that makes a supplier chooser possible. Until it existed `RaisePurchaseOrder` asked a
 * shopkeeper to type the supplier's UUID and said so on the screen.
 *
 * Inactive suppliers come back flagged rather than filtered, for `readStaff`'s reason: an order
 * raised last year names a supplier the shop has since stopped using.
 */
export const readSuppliers = async (config: ReadsConfig): Promise<readonly SupplierRow[]> => {
  const body = await request<{ suppliers?: unknown }>(config, '/suppliers')
  return asList(body.suppliers, 'the suppliers').map((row) => {
    const wire = row as Record<string, unknown>
    const text = (key: string): string | null =>
      wire[key] === undefined || wire[key] === null ? null : String(wire[key])
    const what = 'a supplier'
    return {
      id: wireText(wire, 'id', what),
      name: wireText(wire, 'name', what),
      code: wireText(wire, 'code', what),
      countryCode: wireText(wire, 'countryCode', what),
      currency: wireText(wire, 'currency', what),
      transmissionChannel: text('transmissionChannel'),
      leadTimeDays:
        wire['leadTimeDays'] === undefined || wire['leadTimeDays'] === null
          ? null
          : Number(wire['leadTimeDays']),
      phone: text('phone'),
      email: text('email'),
      isActive: wire['isActive'] === true,
      // No `?? 1`. See `wireNumber`: a defaulted version is a wrong `expectedVersion`, and the
      // write it produces is a 409 that stops the till.
      version: wireNumber(wire, 'version', what),
    }
  })
}

/**
 * `GET /corrections/offers`. §6.33 `COR-001`; §15.25.
 *
 * What may be corrected on this record, and the server's own sentence for each that may not. The
 * panel renders this rather than deriving it: four of the refusals (a closed month, a settled pay
 * period, a counted till, a job on the sale) are not derivable from anything else a read sends,
 * and each one is a 422 that would hold every write queued behind it.
 *
 * `version` comes back here rather than being taken from the order the screen is holding, so the
 * correction cannot be sent with a version that disagrees with the offers it was chosen from.
 */
export const readCorrectionOffers = async (
  config: ReadsConfig,
  recordType: string,
  recordId: string,
): Promise<CorrectionOffersData> => {
  const body = await request<Record<string, unknown>>(
    config,
    `/corrections/offers?recordType=${encodeURIComponent(recordType)}` +
      `&recordId=${encodeURIComponent(recordId)}`,
  )
  return {
    recordId: String(body['recordId']),
    branchId: String(body['branchId']),
    version: Number(body['version']),
    offers: asList(body['offers'], 'what can be corrected').map((row) => {
      const wire = row as Record<string, unknown>
      const target = String(wire['target'])
      return {
        correctionType: String(wire['correctionType']),
        // Narrowed here because the panel branches on it to pick a chooser, and `ARC-020`'s
        // "accept a value you have never heard of" does not help a switch with three arms: an
        // unknown target would render no chooser at all. The server's own set is these three.
        target: (target === 'customer' || target === 'staff' ? target : 'branch') as
          'customer' | 'staff' | 'branch',
        currentValue:
          wire['currentValue'] === undefined || wire['currentValue'] === null
            ? null
            : String(wire['currentValue']),
        currentLabel:
          wire['currentLabel'] === undefined || wire['currentLabel'] === null
            ? null
            : String(wire['currentLabel']),
        offered: wire['offered'] === true,
        ...(wire['code'] === undefined || wire['code'] === null
          ? {}
          : { code: String(wire['code']) }),
        ...(wire['message'] === undefined || wire['message'] === null
          ? {}
          : { message: String(wire['message']) }),
      }
    }),
  }
}

/**
 * `GET /corrections`. `COR-001`'s "both sides show the link".
 *
 * The labels are the server's, not ids resolved here: `previousValue` can name a customer who is
 * in no list this screen has loaded, and the name is encrypted (Appendix N).
 */
export const readCorrections = async (
  config: ReadsConfig,
  recordType: string,
  recordId: string,
): Promise<readonly MadeCorrectionData[]> => {
  const body = await request<{ corrections?: unknown }>(
    config,
    `/corrections?recordType=${encodeURIComponent(recordType)}` +
      `&recordId=${encodeURIComponent(recordId)}`,
  )
  return asList(body.corrections, 'the corrections on this record').map((row) => {
    const wire = row as Record<string, unknown>
    const text = (key: string): string | null =>
      wire[key] === undefined || wire[key] === null ? null : String(wire[key])
    return {
      id: String(wire['id']),
      correctionType: String(wire['correctionType']),
      previousLabel: text('previousLabel'),
      reassignedToLabel: text('reassignedToLabel'),
      ...(text('reason') === null ? {} : { reason: text('reason') as string }),
      correctedByLabel: text('correctedByLabel'),
      ...(text('createdAt') === null ? {} : { createdAt: text('createdAt') as string }),
    }
  })
}

/**
 * `GET /periods`. §6.11 `ACC-008`.
 *
 * What is already locked, so the screen can say "did we do September" rather than only offering
 * the next action. Appendix B's `Period - close` governs both this and the close itself: a month
 * being closed is a fact about the books, not about a branch, so neither takes a `branchId`.
 */
export const readClosedPeriods = async (
  config: ReadsConfig,
): Promise<readonly ClosedPeriodData[]> => {
  const body = await request<{ periods?: unknown }>(config, '/periods')
  return asList(body.periods, 'the closed months').map((row) => {
    const wire = row as Record<string, unknown>
    return {
      period: String(wire['period']),
      closedAt: String(wire['closedAt']),
      closedByStaffId: String(wire['closedByStaffId']),
      reason:
        wire['reason'] === undefined || wire['reason'] === null ? null : String(wire['reason']),
    }
  })
}

/**
 * `GET /stock/holds?customerId`. §6.33 `RES-001`.
 *
 * Live holds and ended ones, because "what happened to the frame I was promised" is the question
 * asked at the counter and an empty list where a hold expired last night answers it wrongly.
 *
 * Mapped rather than cast, like every read here: three screens crashed at runtime on fields the
 * server does not send, all three typechecked, and the mapper is what turns that into a message
 * instead of a white screen.
 */
export const readHolds = async (
  config: ReadsConfig,
  customerId: string,
): Promise<readonly HoldView[]> => {
  const body = await request<{ reservations?: unknown }>(
    config,
    `/stock/holds?customerId=${encodeURIComponent(customerId)}`,
  )
  return asList(body.reservations, 'the holds on file').map((row) => {
    const wire = row as Record<string, unknown>
    const text = (key: string): string | undefined =>
      wire[key] === undefined || wire[key] === null ? undefined : String(wire[key])
    return {
      id: String(wire['id']),
      stockItemId: String(wire['stockItemId']),
      quantity: Number(wire['quantity']),
      expiresAt: String(wire['expiresAt']),
      heldByStaffId: String(wire['heldByStaffId']),
      ...(text('expiredAt') === undefined ? {} : { expiredAt: text('expiredAt') as string }),
      ...(text('releasedAt') === undefined ? {} : { releasedAt: text('releasedAt') as string }),
      ...(text('releaseReason') === undefined
        ? {}
        : { releaseReason: text('releaseReason') as string }),
      version: Number(wire['version']),
      ...(text('label') === undefined ? {} : { label: text('label') as string }),
      timezone: String(wire['timezone']),
    }
  })
}

/**
 * `GET /stock/frames?branchId=&q=`. §6.9 `INV-001`, §6.7 `POS-001`.
 *
 * The read `FrameChoice` needed and nothing had: a frame line could name its stock only through a
 * free-text UUID field, so in practice it named none and the sale left the shelf untouched.
 *
 * Mapped rather than cast, like every read here. The optional fields are passed through only when
 * the server sent them: a frame with no recorded colour is not a frame whose colour is the empty
 * string, and `frameName` joins whatever is there.
 */
export const readFramesForSale = async (
  config: ReadsConfig,
  branchId: string,
  query: string,
): Promise<readonly FrameOption[]> => {
  const body = await request<{ frames?: unknown }>(
    config,
    `/stock/frames?branchId=${encodeURIComponent(branchId)}&q=${encodeURIComponent(query)}`,
  )
  return asList(body.frames, 'the frames on the shelf').map((row) => {
    const wire = row as Record<string, unknown>
    const text = (key: string): string | undefined =>
      wire[key] === undefined || wire[key] === null ? undefined : String(wire[key])
    return {
      stockItemId: String(wire['stockItemId']),
      frameVariantId: String(wire['frameVariantId']),
      brand: String(wire['brand']),
      model: String(wire['model']),
      ...(text('collection') === undefined ? {} : { collection: text('collection') as string }),
      ...(text('colour') === undefined ? {} : { colour: text('colour') as string }),
      ...(text('size') === undefined ? {} : { size: text('size') as string }),
      lot: String(wire['lot']),
      quantity: Number(wire['quantity']),
      ...(wire['onHand'] === undefined ? {} : { onHand: Number(wire['onHand']) }),
      ...(wire['reserved'] === undefined ? {} : { reserved: Number(wire['reserved']) }),
      flags: asList(wire['flags'] ?? [], 'the stock flags').map((flag) => String(flag)),
    }
  })
}

/**
 * `GET /customers/:customerId/erasure-plan`. §6.2 `CUS-006`, `RET-01`, release gate 10.
 *
 * `RET-01`'s first step, as a read: "shows the customer exactly what is retained and why" has to
 * happen before anything executes, so the plan is a separate request and the screen cannot offer
 * the confirmation until it has one.
 *
 * Mapped rather than cast, and `asList` on both halves: a plan with a missing list is a version
 * mismatch, and the screen that renders it is the last thing that should be guessing.
 */
export const readErasurePlan = async (
  config: ReadsConfig,
  customerId: string,
): Promise<ErasurePlanAnswer> => {
  const body = await request<{
    plan?: { toErase?: unknown; toRetain?: unknown; retentionNotice?: unknown; isPartial?: unknown }
    blockedBecause?: unknown
    alreadyErasedAt?: unknown
    queuedSince?: unknown
  }>(config, `/customers/${encodeURIComponent(customerId)}/erasure-plan`)

  const items = (value: unknown, what: string) =>
    asList(value, what).map((row) => {
      const wire = row as Record<string, unknown>
      return {
        kind: String(wire['kind']),
        id: String(wire['id']),
        label: String(wire['label']),
        // Absent means it may be erased, which is the domain's own rule and not a default.
        ...(wire['retainedBecause'] === undefined || wire['retainedBecause'] === null
          ? {}
          : { retainedBecause: String(wire['retainedBecause']) }),
      }
    })

  return {
    plan: {
      toErase: items(body.plan?.toErase, 'what an erasure removes'),
      toRetain: items(body.plan?.toRetain, 'what an erasure keeps'),
      retentionNotice: asList(body.plan?.retentionNotice, 'the retention notice').map((line) =>
        String(line),
      ),
      isPartial: body.plan?.isPartial === true,
    },
    blockedBecause: asList(body.blockedBecause, 'what is in the way').map((line) => String(line)),
    alreadyErasedAt: typeof body.alreadyErasedAt === 'string' ? body.alreadyErasedAt : null,
    queuedSince: typeof body.queuedSince === 'string' ? body.queuedSince : null,
  }
}

/** What `GET /customers/:customerId` sends. Shaped by `customer-detail-read.service.ts`. */
interface WireCustomerDetail {
  readonly id: string
  readonly name: string
  readonly phone?: string
  readonly balance: CustomerDetailData['balance']
  readonly prescriptions?: CustomerDetailData['prescriptions']
  readonly contactLensPrescriptions?: CustomerDetailData['contactLensPrescriptions']
  readonly orders: CustomerDetailData['orders']
  readonly fittings: CustomerDetailData['fittings']
  readonly consents: CustomerDetailData['consents']
}

/**
 * `GET /customers/:customerId`. The read `CustomerDetail.tsx` never had.
 *
 * Mapped rather than cast, like every other read here: the screen shapes a customer differently
 * from the wire, and problem 109 is four screens that guessed a wire shape, typechecked, and
 * crashed. Two fields the screen declares and the server omits on purpose:
 *
 * - `prescriptions` is absent for a caller without Appendix B's `Prescription - view`, and absent
 *   is not an empty list: the screen says "you were not shown these" rather than "there are none".
 * - `recalls` has no table yet, so the server never sends it and the screen labels the section.
 */
export const readCustomerDetail = async (
  config: ReadsConfig,
  customerId: string,
): Promise<CustomerDetailData> => {
  const body = await request<{ customer: WireCustomerDetail }>(
    config,
    `/customers/${encodeURIComponent(customerId)}`,
  )
  const wire = body.customer
  return {
    id: wire.id,
    name: wire.name,
    ...(wire.phone === undefined ? {} : { phone: wire.phone }),
    balance: wire.balance,
    orders: wire.orders,
    fittings: wire.fittings,
    consents: wire.consents,
    // Carried through only when the server sent it. Defaulting to `[]` here would turn "withheld"
    // into "none", which is the one thing the absence is meant to distinguish.
    ...(wire.prescriptions === undefined ? {} : { prescriptions: wire.prescriptions }),
    ...(wire.contactLensPrescriptions === undefined
      ? {}
      : { contactLensPrescriptions: wire.contactLensPrescriptions }),
  }
}

export const readWorkQueue = async (
  config: ReadsConfig,
  branchId: string,
): Promise<readonly WorkQueueGroup[]> => {
  const body = await request<{ groups: readonly WorkQueueGroup[] }>(
    config,
    `/work-queue?branchId=${encodeURIComponent(branchId)}`,
  )
  return body.groups
}

/**
 * `AI-006`'s "visible remaining balance", per task, for this month.
 *
 * A read, so it comes through here and not the queue. Loaded when Mira is opened and not when the
 * app starts: most shifts never open her, and a request on every mount is a request nobody asked for.
 */
export const readMiraQuota = async (
  config: ReadsConfig,
  branchId: string,
): Promise<readonly MiraQuota[]> =>
  mapQuotaList(
    await request<unknown>(config, `/mira/quota?branchId=${encodeURIComponent(branchId)}`),
  )

/**
 * `GET /purchase-orders`. §6.28 `SUP-001` to `SUP-005`, §6.10 `INV-006`.
 *
 * **`overdue` is the server comparing the expected delivery with its own clock** (`SUP-005`), so it
 * arrives on the list and is never recomputed here: a device whose clock is wrong would otherwise
 * chase a supplier who is not late.
 *
 * **An absent `total` or `unitCost` is Appendix B's cost row withheld**, per `ROLE-001a`'s
 * per-branch grant, and the service removes it at the response boundary. A zero would be a price
 * agreed at nothing, which is a different claim about the order.
 */
interface WirePurchaseOrderLine {
  readonly id: string
  readonly sku: string
  readonly description: string
  readonly orderedQuantity: number
  readonly receivedQuantity: number
  readonly backorderQuantity: number
  readonly unitCost?: WireMoney
  readonly lineTotal?: WireMoney
}

interface WirePurchaseOrder {
  readonly id: string
  readonly number: string
  readonly supplierId: string
  readonly status: string
  readonly expectedDeliveryOn?: string
  readonly overdue: boolean
  readonly backorderQuantity: number
  readonly total?: WireMoney
  readonly version: number
  readonly lines: readonly WirePurchaseOrderLine[]
  readonly receipts: readonly { readonly id: string; readonly number: string }[]
  readonly invoices: readonly {
    readonly id: string
    readonly supplierInvoiceNumber: string
    readonly matchStatus: string
    readonly queryReason?: string
    /**
     * `IMP-004`: paid so far and still to pay, in the invoice's own currency.
     *
     * Absent alongside `total` is Appendix B's cost row withheld (`ROLE-001a`), never an invoice
     * nothing has been paid against: the service builds all three and removes all three together.
     */
    readonly settled?: WireMoney
    readonly outstanding?: WireMoney
    /** The invoice's own version, which a payment sends as `expectedVersion`. Never the order's. */
    readonly version: number
  }[]
}

/**
 * `GET /stock/locations`. Where stock can be put at this branch. `BR-10`, `BR-04`.
 *
 * Mapped rather than cast, like every other read here: the three booleans decide what a screen
 * offers, and a missing one arriving as `undefined` would read as "sold from" on a warehouse.
 */
interface WireStockLocation {
  readonly id: string
  readonly name: string
  readonly code: string
  readonly isSelling: boolean
  readonly isCentralWarehouse: boolean
  readonly isTrial: boolean
}

/**
 * `GET /stock/positions` and `GET /stock/items/:id/movements`. §6.10 `INV-001`.
 *
 * **A negative quantity is carried as it is.** §12 case 47 is a sale that drove the shelf below
 * zero, and `negative_stock` is the flag that says so. Clamping it here would hide the one number
 * that means the record and the shelf disagree.
 *
 * **An absent `value` or `unitCost` is Appendix B's cost row withheld** (`ROLE-001a`), and a
 * consignment line is genuinely worth zero in the owned pool (`INV-003`): two different statements
 * that a zero would collapse into one.
 */
interface WireStockPosition {
  readonly stockItemId: string
  readonly locationId: string
  readonly branchId: string
  readonly ownership: string
  /** What it is, in words. Absent when the catalogue row is missing. */
  readonly label?: string
  readonly lot: string
  readonly quantity: number
  readonly flags: readonly string[]
  readonly currency: string
  readonly version: number
  readonly frameVariantId?: string
  readonly lensProductId?: string
  readonly expiryDate?: string
  readonly tray?: string
  readonly value?: WireMoney
}

interface WireStockMovement {
  readonly id: string
  readonly kind: string
  readonly qtyDelta: number
  readonly recordedAt: string
  readonly reason?: string
  readonly refType?: string
  readonly refId?: string
  readonly reversesMovementId?: string
  readonly unitCost?: WireMoney
}

export const readStockPositions = async (
  config: ReadsConfig,
  locationId: string,
): Promise<readonly StockPositionData[]> => {
  const params = new URLSearchParams({ locationId })
  const body = await request<{ readonly positions: readonly WireStockPosition[] }>(
    config,
    `/stock/positions?${params.toString()}`,
  )
  return listOf(body?.positions, 'stock').map((row) => ({
    stockItemId: row.stockItemId,
    ownership: row.ownership,
    lot: row.lot,
    // What it is, in words. The screens printed `frameVariantId`, which is a UUID.
    ...(row.label === undefined ? {} : { label: row.label }),
    quantity: row.quantity,
    flags: row.flags,
    currency: row.currency,
    version: row.version,
    ...(row.frameVariantId === undefined ? {} : { frameVariantId: row.frameVariantId }),
    ...(row.lensProductId === undefined ? {} : { lensProductId: row.lensProductId }),
    ...(row.expiryDate === undefined ? {} : { expiryDate: row.expiryDate }),
    ...(row.tray === undefined ? {} : { tray: row.tray }),
    ...(row.value === undefined ? {} : { valueMinor: row.value.minor }),
  }))
}

export const readStockMovements = async (
  config: ReadsConfig,
  stockItemId: string,
): Promise<readonly StockMovementData[]> => {
  const body = await request<{ readonly movements: readonly WireStockMovement[] }>(
    config,
    `/stock/items/${encodeURIComponent(stockItemId)}/movements`,
  )
  return listOf(body?.movements, 'stock movements').map((row) => ({
    id: row.id,
    kind: row.kind,
    qtyDelta: row.qtyDelta,
    recordedAt: row.recordedAt,
    ...(row.reason === undefined ? {} : { reason: row.reason }),
    ...(row.refType === undefined ? {} : { refType: row.refType }),
    ...(row.reversesMovementId === undefined ? {} : { reversesMovementId: row.reversesMovementId }),
    ...(row.unitCost === undefined
      ? {}
      : { unitCostMinor: row.unitCost.minor, currency: row.unitCost.currency }),
  }))
}

/**
 * What is on the road, in both directions. `INV-004`.
 *
 * **This read could not exist until migration 0046.** A transfer recorded no destination, so there
 * was no column that could answer "what is coming to this branch": the only way to find a transfer
 * was to be told its id. The value is absent rather than zero without Appendix B's cost row
 * (`ROLE-001a`), and a consignment transfer is genuinely zero-valued, which the screen keeps apart.
 */
export const readTransfersInTransit = async (
  config: ReadsConfig,
  branchId: string,
): Promise<readonly TransferData[]> => {
  const body = await request<{ transfers?: unknown }>(
    config,
    `/stock/transfers?branchId=${encodeURIComponent(branchId)}`,
  )
  // `asList` and not `listOf`: the field arrives as `unknown` and the narrowing has to happen
  // here, which is exactly what `listOf` cannot do for an `unknown` without a cast.
  return asList(body?.transfers, 'what is in transit').map((entry) => {
    const row = asRecord(entry, 'a transfer')
    const value = row['value']
    const money = value === undefined || value === null ? undefined : asRecord(value, 'a value')
    return {
      transferId: String(row['transferId']),
      // `ARC-020`: unnarrowed, so a status this build has not met renders as itself.
      status: String(row['status']),
      fromBranchId: String(row['fromBranchId']),
      ...(typeof row['toBranchId'] === 'string' ? { toBranchId: row['toBranchId'] } : {}),
      dispatchedQuantity: Number(row['dispatchedQuantity']),
      dispatchedAt: String(row['dispatchedAt']),
      ...(money === undefined ? {} : { valueMinor: String(money['minor']) }),
      currency: money === undefined ? '' : String(money['currency']),
    }
  })
}

export const readStockLocations = async (
  config: ReadsConfig,
  branchId: string,
): Promise<readonly StockLocationOption[]> => {
  const params = new URLSearchParams({ branchId })
  const body = await request<{ readonly locations: readonly WireStockLocation[] }>(
    config,
    `/stock/locations?${params.toString()}`,
  )
  return listOf(body?.locations, 'stock locations').map((row) => ({
    id: row.id,
    name: row.name,
    code: row.code,
    isSelling: row.isSelling === true,
    isCentralWarehouse: row.isCentralWarehouse === true,
    isTrial: row.isTrial === true,
  }))
}

/**
 * `GET /stock/trial_expiry`. §12 case 118, §6.10 `REF-010`, `INV-009`.
 *
 * **The read that makes case 118 visible to a shopkeeper.** A diagnostic contact lens expiring in a
 * drawer had nothing that could surface it: a sale is what refuses expired stock, and trial stock
 * never has one.
 *
 * `withinDays` is the caller's, with no stored default. `INV-009` leaves the near-expiry window
 * unspecified, so the screen asks and the server refuses a request that does not say.
 *
 * Mapped rather than cast, like every other read here: the two lists are what the panel renders and
 * a missing one arriving as `undefined` would be a white screen rather than an empty list.
 */
export const readTrialExpiry = async (
  config: ReadsConfig,
  branchId: string,
  withinDays: number,
): Promise<TrialExpiryData> => {
  const params = new URLSearchParams({ branchId, withinDays: String(withinDays) })
  const body = await request<Record<string, unknown>>(
    config,
    `/stock/trial_expiry?${params.toString()}`,
  )
  const rows = (value: unknown, what: string): readonly TrialExpiryRow[] =>
    asList(value, what).map((entry) => {
      const row = asRecord(entry, 'a trial item')
      return {
        stockItemId: String(row['stockItemId']),
        lot: String(row['lot'] ?? ''),
        quantity: Number(row['quantity']),
        expiryDate: String(row['expiryDate']),
        locationName: String(row['locationName']),
        locationCode: String(row['locationCode']),
      }
    })
  return {
    today: String(body?.['today'] ?? ''),
    withinDays: Number(body?.['withinDays'] ?? withinDays),
    expired: rows(body?.['expired'], 'expired trial stock'),
    expiringSoon: rows(body?.['expiringSoon'], 'trial stock expiring soon'),
    // Trial items with no expiry date, a trial frame among them. Counted by the server rather than
    // dropped, so the panel can say so instead of implying the drawer holds only what it lists.
    undatedItems: Number(body?.['undatedItems'] ?? 0),
  }
}

/**
 * `GET /price-books`. Every book with its versions. §6.5 `PRC-010`, `PRC-009`.
 *
 * Nothing listed a book before the editor needed one: `publish` took a version id and the loader
 * resolved the one in force for a date, and neither answers "which version is the draft".
 */
export const readPriceBooks = async (config: ReadsConfig): Promise<readonly PriceBookRow[]> => {
  const body = await request<Record<string, unknown>>(config, '/price-books')
  return asList(body?.['books'], 'the price books').map((entry) => {
    const book = asRecord(entry, 'a price book')
    return {
      priceBookId: String(book['priceBookId']),
      name: String(book['name']),
      code: String(book['code']),
      currency: String(book['currency']),
      branchId: typeof book['branchId'] === 'string' ? book['branchId'] : null,
      versions: asList(book['versions'], "a book's versions").map((v) => {
        const row = asRecord(v, 'a price book version')
        return {
          priceBookVersionId: String(row['priceBookVersionId']),
          versionNumber: Number(row['versionNumber']),
          // `ARC-020`: unnarrowed, so a status this build has not met renders as itself.
          status: String(row['status']),
          effectiveFrom: typeof row['effectiveFrom'] === 'string' ? row['effectiveFrom'] : null,
          lensRules: Number(row['lensRules']),
          treatmentRules: Number(row['treatmentRules']),
        }
      }),
    }
  })
}

/**
 * `GET /catalogue/frames?q=`. The frame colours on file. §6.4 `CAT-001`, `CAT-004`.
 *
 * Not `readStockFrames`, which reads the shelf: a frame being photographed has just arrived and may
 * hold no stock, and one whose last unit sold still needs its pictures.
 */
export const readCatalogueFrames = async (
  config: ReadsConfig,
  query = '',
): Promise<readonly CatalogueFrameRow[]> => {
  const params = new URLSearchParams(query === '' ? {} : { q: query })
  const path =
    params.toString() === '' ? '/catalogue/frames' : `/catalogue/frames?${params.toString()}`
  const body = await request<Record<string, unknown>>(config, path)
  return asList(body?.['frames'], 'the frames on file').map((entry) => {
    const row = asRecord(entry, 'a frame')
    return {
      frameVariantId: String(row['frameVariantId']),
      brand: String(row['brand']),
      model: String(row['model']),
      colourCode: String(row['colourCode']),
      colourName: typeof row['colourName'] === 'string' ? row['colourName'] : null,
    }
  })
}

/** `GET /catalogue/variants/:id/images`. Pictures of one frame colour. §6.4 `CAT-004`. */
export const readFrameImages = async (
  config: ReadsConfig,
  frameVariantId: string,
): Promise<readonly FrameImageRow[]> => {
  const body = await request<Record<string, unknown>>(
    config,
    `/catalogue/variants/${frameVariantId}/images`,
  )
  return asList(body?.['images'], "a frame's pictures").map((entry) => {
    const row = asRecord(entry, 'a picture')
    return {
      imageId: String(row['imageId']),
      contentType: String(row['contentType']),
      // A string from the server and kept one: `OPS-209` sums these across a tenant.
      byteSize: String(row['byteSize']),
      angle: typeof row['angle'] === 'string' ? row['angle'] : null,
      position: Number(row['position']),
    }
  })
}

/**
 * `GET /catalogue/variants/:id/images/:imageId/bytes`. §6.4 `CAT-004`.
 *
 * A `Blob` rather than a url, because the bucket is private and the API serves the bytes behind
 * `catalogue_view`: the session's credentials have to be on the request, which a plain `src`
 * attribute cannot do.
 */
export const readFrameImageBytes = async (
  config: ReadsConfig,
  frameVariantId: string,
  imageId: string,
): Promise<Blob> =>
  await requestBlob(config, `/catalogue/variants/${frameVariantId}/images/${imageId}/bytes`)

/** `GET /catalogue/treatments`. The coatings and tints on file. §6.4 `CAT-012`. */
export const readCatalogueTreatments = async (
  config: ReadsConfig,
): Promise<readonly TreatmentRow[]> => {
  const body = await request<Record<string, unknown>>(config, '/catalogue/treatments')
  return asList(body?.['treatments'], 'the coatings on file').map((entry) => {
    const row = asRecord(entry, 'a coating')
    return {
      treatmentId: String(row['treatmentId']),
      kind: String(row['kind']),
      // Null stays null: an AR coating with no grade and a tint with no colour are both ordinary,
      // and an empty string would read as a value somebody typed.
      brand: typeof row['brand'] === 'string' ? row['brand'] : null,
      grade: typeof row['grade'] === 'string' ? row['grade'] : null,
      colour: typeof row['colour'] === 'string' ? row['colour'] : null,
    }
  })
}

/**
 * `GET /catalogue/lenses/:id/treatments`. Which coatings one lens admits. §6.4 `CAT-013`.
 *
 * Both halves, allowed and refused: a refusal with its reason is what a dispenser needs when a
 * customer asks for a coating this lens cannot take, and a list of only the allowed ones would make
 * a no indistinguishable from an answer nobody has given.
 */
export const readLensTreatments = async (
  config: ReadsConfig,
  lensProductId: string,
): Promise<readonly CompatibilityRow[]> => {
  const body = await request<Record<string, unknown>>(
    config,
    `/catalogue/lenses/${lensProductId}/treatments`,
  )
  return asList(body?.['compatibility'], 'the coating rules').map((entry) => {
    const row = asRecord(entry, 'a coating rule')
    return {
      treatmentId: String(row['treatmentId']),
      kind: String(row['kind']),
      brand: typeof row['brand'] === 'string' ? row['brand'] : null,
      grade: typeof row['grade'] === 'string' ? row['grade'] : null,
      colour: typeof row['colour'] === 'string' ? row['colour'] : null,
      allowed: row['allowed'] === true,
      reason: typeof row['reason'] === 'string' ? row['reason'] : null,
      // `ARC-012`: the version a change must send back.
      version: Number(row['version']),
    }
  })
}

/** `GET /catalogue/lenses`. The lens products on file. §6.4 `CAT-010`. */
export const readCatalogueLenses = async (
  config: ReadsConfig,
  query = '',
): Promise<readonly LensRow[]> => {
  const params = new URLSearchParams(query === '' ? {} : { q: query })
  const path =
    params.toString() === '' ? '/catalogue/lenses' : `/catalogue/lenses?${params.toString()}`
  const body = await request<Record<string, unknown>>(config, path)
  return asList(body?.['lenses'], 'the lenses on file').map((entry) => {
    const row = asRecord(entry, 'a lens')
    return {
      lensProductId: String(row['lensProductId']),
      brand: String(row['brand']),
      name: String(row['name']),
      // The server's text, not a number: 1.60 must not become 1.6 through a float.
      refractiveIndex: String(row['refractiveIndex']),
    }
  })
}

/**
 * `GET /messages`. The outbox. §6.17 `MSG-005`.
 *
 * **The body and the subject are the rendered text**, decrypted by the server for a caller holding
 * `message_send_manual`. They are what the screen shows and what a shopkeeper reads out, because
 * `MSG-001a`'s v1 mitigation is the shop sending the message itself.
 *
 * `carriedRxData` is a fact about the row rather than about the template: a message that went out
 * with prescription values in it stays one, whatever the shop has since changed.
 */
interface WireMessage {
  readonly id: string
  readonly kind: string
  readonly channel: string
  readonly status: string
  readonly customerId: string
  readonly locale: string
  readonly scheduledFor: string
  readonly version: number
  readonly attemptCount: number
  readonly carriedRxData: boolean
  readonly subject?: string
  readonly body?: string
  readonly failureReason?: string
  readonly suppressionReason?: string
}

/**
 * `GET /message-templates`. What the shop can say, and what each saying needs. §6.17 `MSG-002`.
 *
 * `valuesRequired` is the server's subtraction, not this layer's: it knows that `enqueueInTx` fills
 * `customer_name` and that nothing fills the rest. A client that worked it out for itself would be
 * a second copy of that rule and would be wrong the day a second value is read from the product.
 */
interface WireMessageTemplate {
  readonly id: string
  readonly kind: string
  readonly channel: string
  readonly locale: string
  readonly isActive: boolean
  readonly body: string
  readonly placeholders: readonly string[]
  readonly valuesRequired: readonly string[]
  readonly embedsRxData: boolean
  readonly version: number
  readonly subject?: string
}

/**
 * `GET /promotions`. Every version of every promotion at this caller's branches. §6.25, Appendix X.
 *
 * **The window arrives as two dates and is flattened here**, because the screen shows a from and a
 * to and `PRM-001` makes both ends inclusive: a nested object for two fields is a shape to
 * remember rather than a fact to read.
 *
 * **An absent `marginFloor` is Appendix B's margin row withheld**, not a promotion with no floor.
 * `gateVersion` removes it at the response boundary per `PRM-004`, and a zero would be a floor of
 * nothing, which is a different instruction to the engine.
 *
 * The condition is carried through untouched. It is Appendix X JSON and the screen hands it back
 * unchanged when it cannot express it, so a mapper that reshaped it would be the one place a
 * revision could silently change what a promotion matches.
 */
interface WirePromotion {
  readonly promotionId: string
  readonly versionNumber: number
  readonly version: number
  readonly branchId: string
  readonly status: string
  readonly currency: string
  readonly withheld?: readonly string[]
  readonly label: string
  readonly condition: unknown
  readonly effect: Record<string, unknown>
  readonly priority: number
  readonly stackable: boolean
  readonly window: { readonly startsOn: string; readonly endsOn: string }
  readonly requiresCoupon: boolean
  readonly marginFloor?: WireMoney
  readonly withdrawReason?: string
}

export const readPromotions = async (config: ReadsConfig): Promise<readonly PromotionData[]> => {
  const body = await request<readonly WirePromotion[]>(config, '/promotions')
  return listOf(body, 'offers').map((row) => ({
    promotionId: row.promotionId,
    versionNumber: row.versionNumber,
    version: row.version,
    branchId: row.branchId,
    status: row.status,
    label: row.label,
    condition: row.condition,
    effect: {
      kind: String(row.effect['kind']),
      ...(row.effect['percent'] === undefined ? {} : { percent: String(row.effect['percent']) }),
      ...(row.effect['amountMinor'] === undefined
        ? {}
        : { amountMinor: String(row.effect['amountMinor']) }),
      ...(row.effect['appliesTo'] === undefined
        ? {}
        : { appliesTo: String(row.effect['appliesTo']) }),
      ...(row.effect['productId'] === undefined
        ? {}
        : { productId: String(row.effect['productId']) }),
      ...(row.effect['category'] === undefined ? {} : { category: String(row.effect['category']) }),
      ...(row.effect['treatmentId'] === undefined
        ? {}
        : { treatmentId: String(row.effect['treatmentId']) }),
      ...(row.effect['fromTier'] === undefined ? {} : { fromTier: String(row.effect['fromTier']) }),
      ...(row.effect['toTier'] === undefined ? {} : { toTier: String(row.effect['toTier']) }),
    },
    priority: row.priority,
    stackable: row.stackable === true,
    startsOn: row.window.startsOn,
    endsOn: row.window.endsOn,
    requiresCoupon: row.requiresCoupon === true,
    // The promotion's own currency, which every amount on it is in. It used to be read out of
    // `marginFloor`, which is optional and gated, so an `amount_off` offer with no floor arrived
    // with no currency and the screen printed its discount as a blank.
    currency: row.currency,
    // `ROLE-001a`: which fields this reader may not see. A screen that cannot tell a withheld
    // floor from no floor erases `PRM-004`'s gate the moment it writes a new version.
    marginFloorWithheld: (row.withheld ?? []).includes('marginFloor'),
    ...(row.marginFloor === undefined ? {} : { marginFloorMinor: row.marginFloor.minor }),
    ...(row.withdrawReason === undefined ? {} : { withdrawReason: row.withdrawReason }),
  }))
}

/**
 * `REF-001` to `REF-006`: the eye tests on one customer's record, newest first. The wire shape is
 * the screen's own (`RefractionData`), so there is nothing to map: the API already dropped what is
 * absent rather than sending nulls.
 */
export const readRefractions = async (
  config: ReadsConfig,
  customerId: string,
): Promise<readonly RefractionData[]> => {
  const body = await request<{ readonly refractions: readonly RefractionData[] }>(
    config,
    `/customers/${encodeURIComponent(customerId)}/refractions`,
  )
  return listOf(body?.refractions, 'eye tests')
}

export const readMessageTemplates = async (
  config: ReadsConfig,
): Promise<readonly MessageTemplateOption[]> => {
  const body = await request<{ readonly templates: readonly WireMessageTemplate[] }>(
    config,
    '/message-templates',
  )
  return listOf(body?.templates, 'message templates').map((row) => ({
    id: row.id,
    kind: row.kind,
    channel: row.channel,
    locale: row.locale,
    isActive: row.isActive === true,
    body: row.body,
    valuesRequired: row.valuesRequired,
    embedsRxData: row.embedsRxData === true,
    version: row.version,
    ...(row.subject === undefined ? {} : { subject: row.subject }),
  }))
}

export const readMessages = async (
  config: ReadsConfig,
  branchId: string,
  status?: string,
): Promise<readonly MessageData[]> => {
  const params = new URLSearchParams({ branchId })
  // Absent is the server's own open set: queued, sending and failed. The client never defines
  // "still to deal with" for itself.
  if (status !== undefined && status !== '') params.set('status', status)
  const body = await request<{ readonly messages: readonly WireMessage[] }>(
    config,
    `/messages?${params.toString()}`,
  )
  return listOf(body?.messages, 'messages').map((row) => ({
    id: row.id,
    kind: row.kind,
    channel: row.channel,
    status: row.status,
    customerId: row.customerId,
    locale: row.locale,
    scheduledFor: row.scheduledFor,
    version: row.version,
    attemptCount: row.attemptCount,
    carriedRxData: row.carriedRxData === true,
    ...(row.subject === undefined ? {} : { subject: row.subject }),
    ...(row.body === undefined ? {} : { body: row.body }),
    ...(row.failureReason === undefined ? {} : { failureReason: row.failureReason }),
    ...(row.suppressionReason === undefined ? {} : { suppressionReason: row.suppressionReason }),
  }))
}

export const readPurchaseOrders = async (
  config: ReadsConfig,
  branchId: string,
  status?: string,
): Promise<readonly PurchaseOrderData[]> => {
  const params = new URLSearchParams({ branchId })
  if (status !== undefined && status !== '') params.set('status', status)
  const body = await request<{ readonly orders: readonly WirePurchaseOrder[] }>(
    config,
    `/purchase-orders?${params.toString()}`,
  )
  return listOf(body?.orders, 'purchase orders').map((row) => ({
    id: row.id,
    number: row.number,
    supplierId: row.supplierId,
    status: row.status,
    overdue: row.overdue,
    backorderQuantity: row.backorderQuantity,
    version: row.version,
    ...(row.expectedDeliveryOn === undefined ? {} : { expectedDeliveryOn: row.expectedDeliveryOn }),
    ...(row.total === undefined
      ? {}
      : { totalMinor: row.total.minor, currency: row.total.currency }),
    lines: row.lines.map((line) => ({
      id: line.id,
      sku: line.sku,
      description: line.description,
      orderedQuantity: line.orderedQuantity,
      receivedQuantity: line.receivedQuantity,
      backorderQuantity: line.backorderQuantity,
      ...(line.unitCost === undefined
        ? {}
        : { unitCostMinor: line.unitCost.minor, currency: line.unitCost.currency }),
    })),
    receipts: row.receipts,
    invoices: row.invoices,
  }))
}

/**
 * `GET /shipments/:shipmentId`. §6.28 `IMP-001` to `IMP-006`, release gate 8.
 *
 * **`costs` and `allocations` are optional because the service deletes them.**
 * `shipmentForCaller` runs `without(view, ['costs', 'allocations'])` for a caller without
 * Appendix B's cost row at the shipment's branch, so an absent key is `ROLE-001a` withheld and an
 * empty array is a shipment carrying no bill yet. The mapper keeps the two apart instead of
 * defaulting either to `[]`: the screen says different things about them, and "no import cost" is
 * a figure somebody prices against.
 *
 * `trackingState`, `kind` and `allocationBasis` stay `string` through here per `ARC-020`. During a
 * migration fan-out a newer tenant schema sends a value this build has no word for, and refusing
 * the read would turn a missing label into an unusable screen.
 *
 * **`basisQuantity` and `basisTotal` are not money and are not touched.** They are `IMP-002`'s
 * audit trail in whatever the basis counts: minor units of goods value, grams, cubic millimetres,
 * or pieces. Putting them through the money formatter would print grams with a currency on them.
 */
interface WireShipmentCost {
  readonly id: string
  readonly kind: string
  readonly payee?: string
  readonly allocationBasis: string
  readonly amount: WireMoney
  readonly baseAmount: WireMoney
  readonly exchangeRate: string
  readonly exchangeRateOn: string
  readonly version: number
}

interface WireShipmentAllocation {
  readonly id: string
  readonly shipmentCostId: string
  readonly goodsReceiptLineId: string
  readonly revision: number
  readonly basis: string
  readonly basisQuantity: string
  readonly basisTotal: string
  readonly share: WireMoney
  readonly capitalised: WireMoney
  readonly cogsAdjustment: WireMoney
  readonly soldQuantity: number
  readonly period?: string
  readonly journalId?: string
}

interface WireShipment {
  readonly id: string
  readonly reference: string
  readonly branchId: string
  readonly carrier?: string
  readonly trackingState: string
  readonly expectedArrivalOn?: string
  readonly costsFinalisedAt?: string
  readonly orders: readonly {
    readonly id: string
    readonly number: string
    readonly status: string
  }[]
  readonly costs?: readonly WireShipmentCost[]
  readonly allocations?: readonly WireShipmentAllocation[]
  readonly version: number
}

export const readShipment = async (
  config: ReadsConfig,
  shipmentId: string,
): Promise<ShipmentData> => {
  const row = await request<WireShipment>(config, `/shipments/${encodeURIComponent(shipmentId)}`)
  return {
    id: row.id,
    reference: row.reference,
    branchId: row.branchId,
    trackingState: row.trackingState,
    version: row.version,
    ...(row.carrier === undefined ? {} : { carrier: row.carrier }),
    ...(row.expectedArrivalOn === undefined ? {} : { expectedArrivalOn: row.expectedArrivalOn }),
    ...(row.costsFinalisedAt === undefined ? {} : { costsFinalisedAt: row.costsFinalisedAt }),
    orders: listOf(row?.orders, 'the orders on this shipment').map((order) => ({
      id: order.id,
      number: order.number,
      status: order.status,
    })),
    // Absent stays absent: this is the `ROLE-001a` gate, and `[]` here would erase it.
    ...(row.costs === undefined
      ? {}
      : {
          costs: listOf(row.costs, 'the bills on this shipment').map((entry) => ({
            id: entry.id,
            kind: entry.kind,
            allocationBasis: entry.allocationBasis,
            amount: entry.amount,
            baseAmount: entry.baseAmount,
            exchangeRate: entry.exchangeRate,
            exchangeRateOn: entry.exchangeRateOn,
            version: entry.version,
            ...(entry.payee === undefined ? {} : { payee: entry.payee }),
          })),
        }),
    ...(row.allocations === undefined
      ? {}
      : {
          allocations: listOf(row.allocations, 'the spread so far').map((entry) => ({
            id: entry.id,
            shipmentCostId: entry.shipmentCostId,
            goodsReceiptLineId: entry.goodsReceiptLineId,
            revision: entry.revision,
            basis: entry.basis,
            basisQuantity: entry.basisQuantity,
            basisTotal: entry.basisTotal,
            share: entry.share,
            capitalised: entry.capitalised,
            cogsAdjustment: entry.cogsAdjustment,
            soldQuantity: entry.soldQuantity,
            ...(entry.period === undefined ? {} : { period: entry.period }),
            ...(entry.journalId === undefined ? {} : { journalId: entry.journalId }),
          })),
        }),
  }
}

/**
 * `GET /purchase-orders` and `GET /purchase-orders/:purchaseOrderId`, shaped for the shipment
 * screen. §6.28 `IMP-001`, `IMP-003`.
 *
 * **A second mapper over the same two endpoints, deliberately.** `readPurchaseOrders` shapes a
 * `PurchaseOrderData` for the purchasing screen, and that type carries none of the three fields
 * this screen is built on: `shipmentId`, a line's `ownership`, and a receipt's own lines. All
 * three decide what gets sent. `shipmentId` is the only route back into a shipment, because there
 * is no `GET /shipments`. `ownership` is what the allocation filters on, and a consignment line
 * sent in comes back `UNKNOWN_LINE`. `receipts[].lines[].goodsReceiptLineId` is what the
 * allocation names, and an owned line left out comes back `SOLD_QUANTITY_REQUIRED`. Both refusals
 * stop the whole queue.
 *
 * Widening `PurchaseOrderData` would hand the purchasing screen four fields it does not render and
 * a second reason to change, so the two reads shape the same wire differently and only the wire
 * interface is shared.
 *
 * **The single read exists because the list is not enough.** `purchasing.service.ts`'s `list`
 * excludes `closed` and `cancelled`, and an order whose goods all arrived and whose supplier
 * invoice matched is closed. Those are exactly the orders a shipment is spread across.
 *
 * **Only the currency comes off `unitCost`, never the amount.** It is there for one check: a bill
 * spread by `value` is refused with `GOODS_VALUE_BASE_UNAVAILABLE` when a line was bought in a
 * currency other than the branch's, because `IMP-004` records a foreign purchase's base amount on
 * the invoice as a whole and nowhere per line. This screen shows no unit cost, so it carries none,
 * and an absent currency means the cost grant is withheld and the check cannot be made.
 */
interface WireShipmentOrderLine {
  readonly id: string
  readonly sku: string
  readonly description: string
  readonly ownership: string
  readonly unitCost?: WireMoney
}

interface WireShipmentReceiptLine {
  readonly goodsReceiptLineId: string
  readonly purchaseOrderLineId: string
  readonly quantityReceived: number
  readonly isOverReceipt: boolean
}

interface WireShipmentOrder {
  readonly id: string
  readonly number: string
  readonly status: string
  readonly version: number
  readonly shipmentId?: string
  readonly lines: readonly WireShipmentOrderLine[]
  readonly receipts: readonly {
    readonly id: string
    readonly number: string
    readonly lines: readonly WireShipmentReceiptLine[]
  }[]
}

const toShipmentOrder = (wire: WireShipmentOrder): ShipmentOrderData => ({
  id: wire.id,
  number: wire.number,
  status: wire.status,
  version: wire.version,
  ...(wire.shipmentId === undefined ? {} : { shipmentId: wire.shipmentId }),
  lines: listOf(wire?.lines, 'the lines on this order').map((line) => ({
    id: line.id,
    sku: line.sku,
    description: line.description,
    ownership: line.ownership,
    ...(line.unitCost === undefined ? {} : { unitCostCurrency: line.unitCost.currency }),
  })),
  receipts: listOf(wire?.receipts, 'the deliveries against this order').map((receipt) => ({
    id: receipt.id,
    number: receipt.number,
    lines: listOf(receipt.lines, 'the lines of this delivery').map((line) => ({
      goodsReceiptLineId: line.goodsReceiptLineId,
      purchaseOrderLineId: line.purchaseOrderLineId,
      quantityReceived: line.quantityReceived,
      isOverReceipt: line.isOverReceipt === true,
    })),
  })),
})

/**
 * `status` is optional for one reason: the list with no status asks for everything **except**
 * `closed` and `cancelled`, and a shipment whose orders have all closed is exactly the one a late
 * duty bill arrives for (`IMP-003`). Without a way to ask for the closed ones, that shipment has no
 * door into it at all, because `OrderView.shipmentId` is the only route back to a shipment.
 * `purchasing.service.ts` refuses a status that is not one of `PURCHASE_ORDER_STATES`, so this is
 * passed through unchanged rather than validated twice.
 */
export const readShipmentOrders = async (
  config: ReadsConfig,
  branchId: string,
  status?: string,
): Promise<readonly ShipmentOrderData[]> => {
  const params = new URLSearchParams({ branchId })
  if (status !== undefined && status !== '') params.set('status', status)
  const body = await request<{ readonly orders: readonly WireShipmentOrder[] }>(
    config,
    `/purchase-orders?${params.toString()}`,
  )
  return listOf(body?.orders, 'purchase orders').map(toShipmentOrder)
}

export const readShipmentOrder = async (
  config: ReadsConfig,
  purchaseOrderId: string,
): Promise<ShipmentOrderData> =>
  toShipmentOrder(
    await request<WireShipmentOrder>(
      config,
      `/purchase-orders/${encodeURIComponent(purchaseOrderId)}`,
    ),
  )

/**
 * `GET /purchase-orders/:id`. §6.10 `INV-006`, §15.6 step 4.
 *
 * **The detail read exists for one field.** `EnterInvoice.lines[]` cites a `goodsReceiptLineId`,
 * and the only place one used to appear was the `POST /receipts` response, which takes
 * `stock_receive`: the dispenser at the dock holds that row and the accountant entering the
 * supplier's paper does not. The ids are now on the order's own receipts, which is why this read
 * can drive the invoice screen and `readPurchaseOrders` cannot.
 *
 * **An absent `total` is the whole cost grant withheld, and takes the currency with it.**
 * `orderForCaller` removes `total` and every `unitCost` from a reader without Appendix B's
 * cost-price row at the branch (`ROLE-001a`), and the order's currency is only ever carried inside
 * a money value. `view()` builds the total unconditionally and every order has one, so absent means
 * withheld with no second reading, and the screen says so rather than printing a zero or guessing a
 * currency it would then parse amounts against.
 *
 * Mapped field by field like every read here, never cast: `Array.isArray` narrows a readonly array
 * to `any[]`, and the nested receipt lines are exactly the shape that silently arrived as
 * `undefined` on three earlier screens.
 */
interface WirePurchaseOrderDetail extends WirePurchaseOrder {
  readonly receipts: readonly {
    readonly id: string
    readonly number: string
    readonly receivedAt: string
    readonly lines: readonly {
      readonly goodsReceiptLineId: string
      readonly purchaseOrderLineId: string
      readonly quantityReceived: number
      readonly isOverReceipt: boolean
      readonly lot?: string
      readonly expiryOn?: string
    }[]
  }[]
}

export const readPurchaseOrder = async (
  config: ReadsConfig,
  purchaseOrderId: string,
): Promise<PurchaseOrderDetailData> => {
  const row = await request<WirePurchaseOrderDetail>(
    config,
    `/purchase-orders/${encodeURIComponent(purchaseOrderId)}`,
  )
  return {
    id: row.id,
    number: row.number,
    status: row.status,
    version: row.version,
    // Both out of the one gated money value, so they are withheld together and never half.
    ...(row.total === undefined
      ? {}
      : { currency: row.total.currency, totalMinor: row.total.minor }),
    lines: listOf(row.lines, 'order lines').map((line) => ({
      id: line.id,
      sku: line.sku,
      description: line.description,
      orderedQuantity: line.orderedQuantity,
      receivedQuantity: line.receivedQuantity,
      ...(line.unitCost === undefined ? {} : { unitCostMinor: line.unitCost.minor }),
    })),
    receipts: listOf(row.receipts, 'deliveries').map((receipt) => ({
      id: receipt.id,
      number: receipt.number,
      lines: listOf(receipt.lines, 'delivery lines').map((receiptLine) => ({
        goodsReceiptLineId: receiptLine.goodsReceiptLineId,
        purchaseOrderLineId: receiptLine.purchaseOrderLineId,
        quantityReceived: receiptLine.quantityReceived,
        isOverReceipt: receiptLine.isOverReceipt === true,
        ...(receiptLine.lot === undefined ? {} : { lot: receiptLine.lot }),
      })),
    })),
    invoices: listOf(row.invoices, 'supplier invoices').map((invoice) => ({
      id: invoice.id,
      supplierInvoiceNumber: invoice.supplierInvoiceNumber,
      matchStatus: invoice.matchStatus,
      ...(invoice.queryReason === undefined ? {} : { queryReason: invoice.queryReason }),
      // `IMP-004`. Mapped and never cast, for this file's own reason: without `outstandingMinor`
      // the screen cannot refuse an over-payment locally, and that refusal is a 422 that holds the
      // whole write queue, so the payment form is not offered at all when the figure is missing.
      ...(invoice.settled === undefined ? {} : { settledMinor: invoice.settled.minor }),
      ...(invoice.outstanding === undefined ? {} : { outstandingMinor: invoice.outstanding.minor }),
      ...(typeof invoice.version === 'number' ? { version: invoice.version } : {}),
    })),
  }
}

/**
 * `GET /repairs`. §6.27 `REP-001` to `REP-004`.
 *
 * **`shopCost` absent is Appendix B's cost row withheld, not a repair that cost nothing.** The
 * service builds the figure unconditionally and removes it at the response boundary, per
 * `ROLE-001a`'s per-branch grant, so an absent field is about the reader. A zero `customerCharge`
 * is the opposite: `REP-004` makes a free adjustment a real answer that must be recorded, because
 * "an unrecorded adjustment is an invisible cost".
 */
interface WireRepair {
  readonly id: string
  readonly customerId: string
  readonly stage: string
  readonly receivedAt: string
  readonly condition: string
  readonly promisedDate: string
  readonly soldByThisShop: boolean
  readonly version: number
  readonly customerCharge?: WireMoney
  readonly shopCost?: WireMoney
  readonly parts: readonly { readonly sku: string; readonly quantity: number }[]
  readonly outcomeDescription?: string
  readonly warrantyClaim?: { readonly component?: string }
}

export const readRepairs = async (
  config: ReadsConfig,
  branchId: string,
  stage?: string,
): Promise<readonly RepairData[]> => {
  const params = new URLSearchParams({ branchId })
  // Absent means the server's open set, which is what is on the bench.
  if (stage !== undefined && stage !== '') params.set('stage', stage)
  const body = await request<{ readonly repairs: readonly WireRepair[] }>(
    config,
    `/repairs?${params.toString()}`,
  )
  return listOf(body?.repairs, 'repairs').map((row) => ({
    id: row.id,
    customerId: row.customerId,
    stage: row.stage,
    condition: row.condition,
    promisedDate: row.promisedDate,
    soldByThisShop: row.soldByThisShop,
    version: row.version,
    parts: row.parts,
    // `'customerCharge' in row` would be the same here, but the money needs splitting either way.
    ...(row.customerCharge === undefined
      ? {}
      : {
          chargeMinor: need(row.customerCharge, 'customerCharge').minor,
          currency: row.customerCharge.currency,
        }),
    ...(row.shopCost === undefined ? {} : { shopCostMinor: row.shopCost.minor }),
    ...(row.outcomeDescription === undefined ? {} : { outcomeDescription: row.outcomeDescription }),
    ...(row.warrantyClaim === undefined ? {} : { warrantyComponent: row.warrantyClaim.component }),
  }))
}

/**
 * `GET /recalls`. §6.24 `APT-004` to `APT-006`.
 *
 * **`lapsed` is the server comparing `supply_ends_on` against its own clock**, which `APT-006`
 * calls a safety issue rather than a reminder: a contact lens wearer whose supply ran out before
 * today is wearing lenses they have run out of. It is never sent on a write response, for the same
 * reason the quote path gives about its own `lapsed`, so the screen reads it from the list.
 *
 * **`withheldReason` is a recorded answer, not a skipped row.** `APT-005` keeps the entry with the
 * reason consent or suppression said no, because "we did not contact this customer and here is
 * why" is the only question anybody asks about a recall that never arrived.
 */
interface WireRecallEntry {
  readonly id: string
  readonly customerId: string
  readonly kind: string
  readonly status: string
  readonly dueOn: string
  readonly sendOn: string
  readonly nudgeNumber: number
  readonly version: number
  readonly supplyEndsOn?: string
  readonly lapsed?: boolean
  readonly channel?: string
  readonly withheldReason?: string
  readonly sourceOrderId?: string
  readonly convertedOrderId?: string
}

export const readRecalls = async (
  config: ReadsConfig,
  branchId: string,
  status?: string,
): Promise<readonly RecallEntryData[]> => {
  const params = new URLSearchParams({ branchId })
  // Absent means the server's own open set, which is what the queue is. Sent only when somebody
  // asks to look at one status, so the client never defines "open" for itself.
  if (status !== undefined && status !== '') params.set('status', status)
  const body = await request<{ readonly entries: readonly WireRecallEntry[] }>(
    config,
    `/recalls?${params.toString()}`,
  )
  return listOf(body?.entries, 'recalls').map((row) => ({
    id: row.id,
    customerId: row.customerId,
    kind: row.kind,
    status: row.status,
    dueOn: row.dueOn,
    sendOn: row.sendOn,
    nudgeNumber: row.nudgeNumber,
    version: row.version,
    lapsed: row.lapsed ?? false,
    ...(row.supplyEndsOn === undefined ? {} : { supplyEndsOn: row.supplyEndsOn }),
    ...(row.channel === undefined ? {} : { channel: row.channel }),
    ...(row.withheldReason === undefined ? {} : { withheldReason: row.withheldReason }),
    ...(row.sourceOrderId === undefined ? {} : { sourceOrderId: row.sourceOrderId }),
    ...(row.convertedOrderId === undefined ? {} : { convertedOrderId: row.convertedOrderId }),
  }))
}

/**
 * `GET /notifications`. §6.26 `MSG-005a`.
 *
 * **One status per call, which is the endpoint's shape and not a convenience.** The server
 * defaults to `unread` and takes one status, so the screen asks for the list it wants rather than
 * filtering a longer one: `unread` is what somebody has to act on, and a client that fetched all
 * three and filtered would be deciding what counts as outstanding.
 *
 * `staffId` absent means the notification is the branch's rather than one person's, and the server
 * already filters to `staff_id is null or staff_id = me`, so everything returned is this reader's
 * business.
 */
interface WireNotification {
  readonly id: string
  readonly kind: string
  readonly status: string
  readonly branchId: string
  readonly summary: string
  readonly version: number
  readonly staffId?: string
  readonly sourceType?: string
  readonly sourceId?: string
}

export const readNotifications = async (
  config: ReadsConfig,
  branchId: string,
  status: 'unread' | 'read' | 'dismissed',
): Promise<readonly NotificationData[]> => {
  const body = await request<{ readonly notifications: readonly WireNotification[] }>(
    config,
    `/notifications?branchId=${encodeURIComponent(branchId)}&status=${status}`,
  )
  return listOf(body?.notifications, 'notifications').map((row) => ({
    id: row.id,
    kind: row.kind,
    status: row.status,
    summary: row.summary,
    version: row.version,
    ...(row.staffId === undefined ? {} : { staffId: row.staffId }),
    ...(row.sourceType === undefined ? {} : { sourceType: row.sourceType }),
    ...(row.sourceId === undefined ? {} : { sourceId: row.sourceId }),
  }))
}

/**
 * `GET /reports` and `GET /reports/:id`. `RPT-01` to `RPT-11`, Appendix Z.
 *
 * **The listing is the schema.** Each entry carries the report's columns and parameters, so the
 * screen renders any of the twenty-one without knowing one of them by name: `kind` decides how a
 * cell is formatted and `parameters` decides what to ask for before running it. `available` is the
 * server saying which of Appendix Z's twenty-two it has implemented, and `permission` is which of
 * Appendix B's two report rows it needs, so a report a role cannot run never appears.
 *
 * **A run is `{ rows }` plus whatever that report adds**, which is why the row type is
 * `Record<string, unknown>` and the column `kind` is what makes it printable. Typing twenty-one
 * response shapes here would be twenty-one more things to keep in step with the appendix, and the
 * columns already say what is in a row.
 *
 * `withheld` appears on the reports that carry cost or margin (`ROLE-001a`), in the server's own
 * field names, and is shown rather than silently leaving a column blank.
 */
interface WireReportListing {
  readonly reports: readonly {
    readonly id: string
    readonly title: string
    readonly class: 'financial' | 'operational'
    readonly permission: string
    readonly available: boolean
    readonly columns: readonly {
      readonly key: string
      readonly label: string
      readonly kind: string
    }[]
    readonly parameters: readonly { readonly key: string; readonly defaultValue?: number }[]
  }[]
}

export const readReportListing = async (config: ReadsConfig): Promise<ReportListing> => {
  const body = await request<WireReportListing>(config, '/reports')
  return {
    reports: listOf(body?.reports, 'reports').map((report) => ({
      id: report.id,
      title: report.title,
      reportClass: report.class,
      available: report.available,
      columns: report.columns,
      parameters: report.parameters,
    })),
  }
}

export const readReport = async (
  config: ReadsConfig,
  reportId: string,
  parameters: Readonly<Record<string, string>>,
): Promise<ReportRun> => {
  const query = new URLSearchParams(parameters).toString()
  const body = await request<Record<string, unknown>>(
    config,
    `/reports/${encodeURIComponent(reportId)}${query === '' ? '' : `?${query}`}`,
  )
  const rows = body?.['rows']
  if (!Array.isArray(rows)) {
    throw new ReadFailed(
      500,
      'READ_CONTRACT',
      `The ${reportId} report came back without any rows field. This is a version mismatch, not your fault.`,
    )
  }
  const withheld = body['withheld']
  return {
    rows: rows as readonly Record<string, unknown>[],
    // Everything the report added beside its rows: `asOf`, `branchId`, a period. Carried as given
    // so the screen can print what the figures are as of, which `RPT-09` makes part of the report.
    meta: Object.fromEntries(
      Object.entries(body).filter(([key]) => key !== 'rows' && key !== 'withheld'),
    ),
    ...(Array.isArray(withheld) ? { withheld: withheld as readonly string[] } : {}),
  }
}

/**
 * `GET /price-books/versions/:id/simulation`. `PRC-012`.
 *
 * A read, so it is here and not in the write queue. Money is `{ minor, currency }` strings and is
 * passed through untouched: the screen formats it with `money.ts` and sums nothing.
 */
interface WireSimulation {
  readonly windowDays?: number
  readonly lineCap?: number
  readonly truncated?: boolean
  readonly totals?: PriceImpactData['totals']
  readonly products?: readonly PriceImpactData['products'][number][]
  readonly errors?: readonly {
    readonly productId: string
    readonly side: 'baseline' | 'candidate'
    readonly code: string
    readonly message: string
  }[]
  readonly skipped?: readonly {
    readonly productId: string
    readonly code: string
    readonly message: string
  }[]
}

export const readPriceImpact = async (
  config: ReadsConfig,
  versionId: string,
  rounding: RoundingMode,
): Promise<PriceImpactData> => {
  const body = await request<WireSimulation>(
    config,
    `/price-books/versions/${encodeURIComponent(versionId)}/simulation?rounding=${encodeURIComponent(rounding)}`,
  )
  if (
    body === undefined ||
    body.totals === undefined ||
    typeof body.windowDays !== 'number' ||
    typeof body.lineCap !== 'number'
  ) {
    throw new ReadFailed(
      500,
      'READ_CONTRACT',
      'The price preview came back without its totals. This is a version mismatch, not your fault.',
    )
  }
  return {
    windowDays: body.windowDays,
    lineCap: body.lineCap,
    truncated: body.truncated === true,
    totals: body.totals,
    products: listOf(body.products, 'products'),
    errors: listOf(body.errors, 'errors').map((e) => ({
      productId: e.productId,
      side: e.side,
      code: e.code,
      message: e.message,
    })),
    skipped: listOf(body.skipped, 'skipped lines').map((s) => ({
      productId: s.productId,
      code: s.code,
      message: s.message,
    })),
  }
}

/**
 * The wire shape of `GET /selection/lenses`, which is `LensSelectionResponse`. §6.30 `DSS-001`.
 *
 * **`cost`, `margin` and `marginRank` are absent rather than null when withheld**, and the two mean
 * different things: null is "no connected supplier quoted it" and absent is "you may not see it"
 * (`ROLE-001a`, a per-branch grant). `withheld` names which, so the screen can say "you do not have
 * cost visibility here" instead of showing a blank column that reads as missing data. The mapper
 * keeps that distinction rather than collapsing both to undefined.
 *
 * `rankedBy` says which key the server ordered by, because a margin ranking is itself a
 * disclosure: a dispenser without the grant gets a price ranking and has to be told so, or the
 * order of the list is a fact they cannot account for.
 */
interface WireLensOption {
  readonly position: number
  readonly productId: string
  readonly name: string
  readonly price: WireMoney
  readonly priceRank: number
  readonly warnings: readonly ChoiceFinding[]
  readonly cost?: WireMoney | null
  readonly costSupplier?: { readonly id: string; readonly name: string } | null
  readonly margin?: WireMoney | null
  readonly marginPct?: { readonly milliPercent: string } | null
  readonly marginRank?: number | null
}

interface WireLensSelection {
  readonly branchId: string
  readonly prescriptionId: string
  readonly rxType: string
  readonly currency: string
  readonly priceBookVersionId: string
  readonly rankedBy: 'margin' | 'price'
  readonly options: readonly WireLensOption[]
  readonly withheld: readonly string[]
  readonly unpriced: readonly {
    readonly productId: string
    readonly name: string
    readonly code: string
    readonly message: string
  }[]
  readonly unavailable: readonly {
    readonly productId: string
    readonly name: string
    readonly findings: readonly ChoiceFinding[]
  }[]
}

const toLensSelection = (wire: WireLensSelection): LensSelectionData => ({
  rxType: wire.rxType,
  currency: wire.currency,
  rankedBy: wire.rankedBy,
  withheld: wire.withheld,
  options: wire.options.map((option) => ({
    productId: option.productId,
    name: option.name,
    priceMinor: need(option.price, `price of ${option.name}`).minor,
    priceRank: option.priceRank,
    warnings: option.warnings,
    // `'cost' in option` rather than a null check: absent is withheld and null is unquoted, and
    // the screen says something different for each.
    ...('cost' in option
      ? { costMinor: option.cost === null ? null : (option.cost?.minor ?? null) }
      : {}),
    ...(option.costSupplier === undefined
      ? {}
      : { costSupplierName: option.costSupplier === null ? null : option.costSupplier.name }),
    ...('margin' in option
      ? { marginMinor: option.margin === null ? null : (option.margin?.minor ?? null) }
      : {}),
  })),
  unpriced: wire.unpriced.map((row) => ({
    productId: row.productId,
    name: row.name,
    message: row.message,
  })),
  unavailable: wire.unavailable.map((row) => ({
    productId: row.productId,
    name: row.name,
    findings: row.findings,
  })),
})

export interface LensSelectionQuery {
  readonly branchId: string
  readonly prescriptionId: string
  readonly rounding: RoundingMode
  readonly treatmentIds?: readonly string[]
  readonly frameVariantId?: string
  readonly fittingHeightMm?: number
  /**
   * `CAT-015` and `DSP-004`: the fitting measurement to check cut-out against. Without it the
   * server skips the whole check, so a caller that never sends it shows no cut-out warning at all.
   */
  readonly fittingMeasurementId?: string
}

export const readLensSelection = async (
  config: ReadsConfig,
  query: LensSelectionQuery,
): Promise<LensSelectionData> => {
  const params = new URLSearchParams({
    branchId: query.branchId,
    prescriptionId: query.prescriptionId,
    rounding: query.rounding,
  })
  // Only when set: the endpoint distinguishes an absent treatment list from an empty one, because
  // an empty one is "no treatments" and an absent one is "do not filter on treatments".
  if (query.treatmentIds !== undefined && query.treatmentIds.length > 0) {
    params.set('treatmentIds', query.treatmentIds.join(','))
  }
  if (query.frameVariantId !== undefined) params.set('frameVariantId', query.frameVariantId)
  if (query.fittingMeasurementId !== undefined) {
    params.set('fittingMeasurementId', query.fittingMeasurementId)
  }
  if (query.fittingHeightMm !== undefined) {
    params.set('fittingHeightMm', String(query.fittingHeightMm))
  }
  return toLensSelection(
    await request<WireLensSelection>(config, `/selection/lenses?${params.toString()}`),
  )
}

/**
 * The wire shape of `GET /orders/:orderId/receipt`, which is `ReceiptView`.
 *
 * **Every refusal arrives as a `ReadFailed` with the domain's own code**, because the gate is on the
 * server: `RECEIPT_NOT_AN_INVOICE` on a draft, `RECEIPT_SALE_REVERSED` on a void,
 * `RECEIPT_NO_TAX_REGISTRATION` on a branch that has not been given one. The screen shows the
 * sentence rather than translating the code, which is the same choice `statusLine` makes about a
 * refused write: the server's message names the specific thing, and a generic translation of it
 * would lose which of the three happened.
 *
 * `absent` is Appendix L's own English wording, for the person deciding whether to print rather
 * than for the slip. The screen keeps it off the paper.
 */
interface WireReceiptLine {
  readonly id: string
  readonly description: string
  readonly quantity: number
  readonly linePrice: WireMoney
  readonly unitPrice?: WireMoney
  readonly tax: WireMoney
  readonly gross: WireMoney
}

interface WireReceipt {
  readonly orderId: string
  readonly status: string
  readonly shop: {
    readonly name: string
    readonly addressLines: readonly string[]
    readonly taxRegistration?: string
  }
  readonly invoiceNumber: string
  readonly seriesCode: string
  readonly issuedAt: string
  readonly servedByName: string
  readonly lines: readonly WireReceiptLine[]
  readonly subtotal: WireMoney
  readonly orderDiscount: WireMoney
  readonly tax: WireMoney
  readonly total: WireMoney
  readonly tenders: readonly { readonly method: string; readonly amount: WireMoney }[]
  readonly tendered: WireMoney
  readonly refunded: WireMoney
  readonly balanceDue: WireMoney
  readonly settled: boolean
  readonly promisedOn?: string
  readonly policyVersion?: string
  readonly verificationNote?: string
  /** `TAX-005`: present only for a confirmed fiscal record carrying both the number and the QR. */
  readonly fbr?: { readonly invoiceNumber: string; readonly qrPayload: string }
  readonly absent: readonly string[]
}

const toReceiptData = (wire: WireReceipt): ReceiptData => ({
  orderId: wire.orderId,
  status: wire.status,
  shop: wire.shop,
  invoiceNumber: wire.invoiceNumber,
  seriesCode: wire.seriesCode,
  issuedAt: wire.issuedAt,
  servedByName: wire.servedByName,
  currency: need(wire.total, 'total').currency,
  lines: wire.lines.map((line) => ({
    id: line.id,
    description: line.description,
    quantity: line.quantity,
    linePriceMinor: need(line.linePrice, 'line price').minor,
    ...(line.unitPrice === undefined ? {} : { unitPriceMinor: line.unitPrice.minor }),
    taxMinor: need(line.tax, 'line tax').minor,
    grossMinor: need(line.gross, 'line gross').minor,
  })),
  subtotalMinor: need(wire.subtotal, 'subtotal').minor,
  orderDiscountMinor: need(wire.orderDiscount, 'orderDiscount').minor,
  taxMinor: need(wire.tax, 'tax').minor,
  totalMinor: wire.total.minor,
  tenders: wire.tenders.map((tender) => ({
    method: tender.method,
    amountMinor: need(tender.amount, 'tender amount').minor,
  })),
  tenderedMinor: need(wire.tendered, 'tendered').minor,
  refundedMinor: need(wire.refunded, 'refunded').minor,
  balanceDueMinor: need(wire.balanceDue, 'balanceDue').minor,
  settled: wire.settled,
  ...(wire.promisedOn === undefined ? {} : { promisedOn: wire.promisedOn }),
  ...(wire.policyVersion === undefined ? {} : { policyVersion: wire.policyVersion }),
  ...(wire.verificationNote === undefined ? {} : { verificationNote: wire.verificationNote }),
  // Through the mapper like every other optional field, so an older server that does not send it
  // leaves the slip without a fiscal block rather than with an empty one.
  ...(wire.fbr === undefined ? {} : { fbr: wire.fbr }),
  absent: wire.absent,
})

export const readReceipt = async (config: ReadsConfig, orderId: string): Promise<ReceiptData> =>
  toReceiptData(
    await request<WireReceipt>(config, `/orders/${encodeURIComponent(orderId)}/receipt`),
  )

/**
 * `GET /prescriptions/:prescriptionId/copy`. `RXW-003`, Appendix L.
 *
 * The domain's own type, for the same reason the job ticket uses it: `buildPrescriptionCopy`
 * produces exactly this shape and the route returns it unchanged, so a second declaration here
 * would be two things to keep in step with a cast between them.
 *
 * Every power on it is a signed two-decimal string. A `Dioptre` is an integer count of
 * quarter-dioptres, and a prescription carrying -9 where -2.25 was meant is one another dispenser
 * grinds wrongly.
 */
export type PrescriptionCopyData = PrescriptionCopy

export const readPrescriptionCopy = async (
  config: ReadsConfig,
  prescriptionId: string,
): Promise<PrescriptionCopyData> =>
  request<PrescriptionCopyData>(config, `/prescriptions/${encodeURIComponent(prescriptionId)}/copy`)

/**
 * `GET /jobs/:jobId/ticket`. `JOB-011`, Appendix L.
 *
 * **The domain's own type, not a copy of it.** `buildJobTicket` produces exactly this shape and the
 * route returns it unchanged, so re-declaring it here would be two definitions that have to be kept
 * in step, and the thing keeping them in step would be a cast. Importing it makes a drift a type
 * error, and lets this screen call `renderJobOrderText` on what it was handed rather than
 * reimplementing it.
 *
 * Every power on it is a signed two-decimal string, not a number: a `Dioptre` is an integer count
 * of quarter-dioptres, so -2.00 D is the number -8, and a lab document carrying -8 is a lens ground
 * at the wrong power.
 *
 * No mapper, because there is nothing to reshape. The server already decided what the document says
 * and `absent` carries what it could not supply; a mapper here would be a second place that could
 * drop a mandatory Appendix L field.
 */
export type JobTicketData = JobTicket

export const readJobTicket = async (config: ReadsConfig, jobId: string): Promise<JobTicketData> =>
  request<JobTicketData>(config, `/jobs/${encodeURIComponent(jobId)}/ticket`)

/**
 * The wire shape of `GET /quotes/:quoteId`, which is `QuoteView` in `quotes.service.ts`.
 *
 * `lapsed` is the field worth naming. The server sets it on a read and never on a write response,
 * because a replayed idempotent body would freeze a verdict about the clock. So a quote still
 * reading `quoted` past its expiry arrives with `lapsed: true`, and the screen has to say that
 * rather than offer a conversion the server will refuse with a 409.
 */
interface WireQuoteLine {
  readonly id: string
  readonly position: number
  readonly pairNumber?: number
  readonly eye?: string
  readonly description: string
  readonly quantity: number
  readonly gross: WireMoney
  readonly explanation: readonly PriceExplanation[]
}

interface WireQuote {
  readonly id: string
  readonly branchId: string
  readonly customerId: string
  readonly saleType: string
  readonly status: string
  readonly lapsed?: boolean
  readonly issuedAt: string
  readonly expiresAt: string
  readonly orderDiscount: WireMoney
  readonly subtotal: WireMoney
  readonly tax: WireMoney
  readonly total: WireMoney
  readonly conversion?: { readonly orderId: string; readonly convertedAt: string }
  readonly lines: readonly WireQuoteLine[]
  readonly version: number
}

const toQuoteData = (wire: WireQuote): QuoteData => ({
  id: wire.id,
  customerId: wire.customerId,
  saleType: wire.saleType,
  status: wire.status,
  // `?? false` and not `=== true`: absent means the server did not compute it, which on this
  // endpoint it always does. Either way the screen needs a boolean, never `undefined` reaching a
  // branch that decides whether to offer a conversion.
  lapsed: wire.lapsed ?? false,
  issuedAt: wire.issuedAt,
  expiresAt: wire.expiresAt,
  currency: need(wire.total, 'total').currency,
  subtotalMinor: need(wire.subtotal, 'subtotal').minor,
  orderDiscountMinor: need(wire.orderDiscount, 'orderDiscount').minor,
  taxMinor: need(wire.tax, 'tax').minor,
  totalMinor: wire.total.minor,
  ...(wire.conversion === undefined ? {} : { convertedToOrderId: wire.conversion.orderId }),
  lines: wire.lines.map((line) => ({
    id: line.id,
    description: line.description,
    quantity: line.quantity,
    grossMinor: need(line.gross, `line ${String(line.position)} gross`).minor,
    ...(line.pairNumber === undefined ? {} : { pairNumber: line.pairNumber }),
    ...(line.eye === undefined ? {} : { eye: line.eye }),
    // `PRC-011`: "a visible price explanation on every quote". Absent rather than empty, so the
    // screen renders nothing rather than an empty disclosure.
    ...(line.explanation.length === 0 ? {} : { priceExplanation: line.explanation }),
  })),
  version: wire.version,
})

export const readQuote = async (config: ReadsConfig, quoteId: string): Promise<QuoteData> =>
  toQuoteData(await request<WireQuote>(config, `/quotes/${encodeURIComponent(quoteId)}`))

/**
 * `DSP-005`: an incomplete dispense, "saved incomplete and resumed by another staff member on
 * another terminal".
 *
 * The list is per branch rather than per person, and that is the requirement rather than a
 * convenience: a customer who comes back at four o'clock is served by whoever is on the floor, and
 * a draft only the person who started it can see is a draft that gets started again from nothing.
 * `savedByStaffId` and `savedAt` come back so the screen can say who left it and when, because
 * picking up somebody's half-finished sale without knowing whose it is, is how two people serve the
 * same customer.
 */
export interface OpenDispense {
  readonly id: string
  readonly branchId: string
  readonly customerId?: string
  readonly prescriptionId?: string
  /**
   * The wire type, `string`, for the reason `readJob` passes a status through unchanged: during a
   * migration fan-out (`ARC-020`) a newer tenant schema can send a value this build has no word
   * for, and narrowing it here would turn a label problem into a refused read. The screen shows an
   * unrecognised value as itself rather than blank, which is the half that was missing.
   */
  readonly saleType?: string
  readonly choices: Record<string, unknown>
  readonly quoteId?: string
  readonly startedByStaffId: string
  readonly savedByStaffId: string
  /** `staff.display_name`, absent when the saver was the seeder or a migration. */
  readonly savedByName?: string
  readonly savedAt: string
  readonly version: number
}

export const readOpenDispenses = async (
  config: ReadsConfig,
  branchId: string,
): Promise<readonly OpenDispense[]> => {
  const body = await request<{ dispenses: readonly OpenDispense[] }>(
    config,
    `/dispenses?branchId=${encodeURIComponent(branchId)}`,
  )
  // Checked, and not with `need`, whose message names the order detail. This guards the whole
  // response rather than one row: an absent list would otherwise read as an empty branch, which is
  // the wrong thing to tell someone looking for a sale they know they left half-done. One
  // unrecognised field on one row is deliberately not an error here, per `saleType` above.
  return listOf(body?.dispenses, 'unfinished dispenses')
}

export const readDispense = async (
  config: ReadsConfig,
  dispenseId: string,
): Promise<OpenDispense> =>
  request<OpenDispense>(config, `/dispenses/${encodeURIComponent(dispenseId)}`)

/**
 * The two narrowings the commission definition needs, as the server's own codec writes them.
 *
 * `commission-codec.ts` has exactly this `isRecord` and refuses a malformed definition with a 422
 * naming the field. The definition arrives here as `Record<string, unknown>` and has to be taken
 * apart field by field, so the alternative is a cast at every field, which is what the mapper layer
 * exists to prevent: three screens crashed at runtime on fields the server does not send and all
 * three typechecked.
 *
 * A list is narrowed by its declared return type rather than by a cast, for the reason `listOf`
 * gives in its own docstring: `Array.isArray` narrows `unknown` to `any[]`, and every field read
 * out of an `any` afterwards is unchecked again.
 */
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const asRecord = (value: unknown, what: string): Record<string, unknown> => {
  if (!isRecord(value)) {
    throw new ReadFailed(
      500,
      'READ_CONTRACT',
      `The server did not send ${what} as an object. This is a version mismatch, not your fault.`,
    )
  }
  return value
}

const asList = (value: unknown, what: string): readonly unknown[] => {
  if (!Array.isArray(value)) {
    throw new ReadFailed(
      500,
      'READ_CONTRACT',
      `The server did not send ${what} as a list. This is a version mismatch, not your fault.`,
    )
  }
  return value
}

/** A field that is present, as text. Absent and null are both absent: never "undefined". */
const optionalText = (value: unknown): string | undefined =>
  value === undefined || value === null ? undefined : String(value)

/**
 * `GET /commission/schemes`. Every version of every scheme at this caller's branches. §6.24.
 *
 * **A bare array, like `GET /promotions`.** `CommissionController.list` returns the service's own
 * array rather than wrapping it, so this maps `body` and not `body.schemes`.
 *
 * **`rules` is an object keyed by the line kind and the screen wants a list**, because the screen
 * shows a row per kind and a form row per kind, and iterating an object in render order means
 * iterating whatever order Postgres happened to store the jsonb keys in. Sorted by the kind here so
 * the same scheme reads the same way twice, which jsonb's own ordering (length, then bytewise) does
 * not promise across a rewrite.
 *
 * **Every bigint stays a decimal string.** An amount is minor units and a rate is thousandths of a
 * percent, both of which can exceed `Number.MAX_SAFE_INTEGER` and neither of which survives
 * `Number()` exactly. `money.ts` turns the first into a `bigint` and `CommissionSchemes.tsx` owns
 * the second: this file never arithmetics either.
 *
 * **`metricRules` is carried element by element, untouched.** `COM-005` is out of this slice and a
 * revision posts the whole definition back, so a mapper that reshaped a bonus would be the one
 * place a revision could silently change what the shop pays. The screen reads only how many there
 * are.
 *
 * **Nothing is gated on this endpoint**, unlike a promotion's margin floor: a scheme's figures are
 * the pay rules themselves, and `tenant_settings_policies` is owner only, so there is no withheld
 * field here to keep apart from a zero.
 */
interface WireSchemeVersion {
  readonly id: string
  readonly schemeId: string
  readonly versionNumber: number
  readonly version: number
  readonly branchId: string
  readonly currency: string
  readonly status: string
  readonly label: string
  /** Appendix H's jsonb definition: `rounding`, `earnsOn`, `attributionOrder`, `rules`, `tier`, `metricRules`, `remake`. */
  readonly definition: Record<string, unknown>
  readonly publishedAt?: string
  readonly withdrawnAt?: string
  readonly withdrawReason?: string
}

const toSchemeRules = (raw: unknown): readonly SchemeRuleData[] =>
  Object.entries(asRecord(raw, 'the pay rules'))
    .map(([lineKind, value]): SchemeRuleData => {
      const rule = asRecord(value, `the rule for ${lineKind}`)
      const byTier = rule['amountByTier']
      return {
        lineKind,
        basis: String(rule['basis']),
        ...(rule['amountMinor'] === undefined ? {} : { amountMinor: String(rule['amountMinor']) }),
        ...(rule['rateMilliPercent'] === undefined
          ? {}
          : { rateMilliPercent: String(rule['rateMilliPercent']) }),
        ...(rule['base'] === undefined ? {} : { base: String(rule['base']) }),
        ...(rule['negativeMargin'] === undefined
          ? {}
          : { negativeMargin: String(rule['negativeMargin']) }),
        ...(byTier === undefined
          ? {}
          : {
              amountByTier: Object.entries(asRecord(byTier, `the lens tiers for ${lineKind}`)).map(
                ([tier, amount]) => ({ tier, amountMinor: String(amount) }),
              ),
            }),
      }
    })
    // Stable, because jsonb does not keep the order the owner typed them in.
    .sort((a, b) => (a.lineKind < b.lineKind ? -1 : a.lineKind > b.lineKind ? 1 : 0))

const toSchemeTier = (raw: unknown): SchemeTierData => {
  const tier = asRecord(raw, 'the threshold rule')
  return {
    scope: String(tier['scope']),
    measure: String(tier['measure']),
    mode: String(tier['mode']),
    steps: asList(tier['steps'], 'the thresholds').map((value, i) => {
      const step = asRecord(value, `threshold ${String(i + 1)}`)
      return {
        thresholdMinor: String(step['thresholdMinor']),
        // `=== true` and not truthiness: the one field here that is a boolean on the wire, and a
        // missing one has to read as "only above the threshold" rather than as a string.
        inclusive: step['inclusive'] === true,
        rateMilliPercent: String(step['rateMilliPercent']),
      }
    }),
  }
}

export const readCommissionSchemes = async (
  config: ReadsConfig,
): Promise<readonly CommissionSchemeVersionData[]> => {
  const body = await request<readonly WireSchemeVersion[]>(config, '/commission/schemes')
  return listOf(body, 'pay rules').map((row) => {
    const definition = asRecord(row.definition, 'the pay rules')
    const tier = definition['tier']
    const metricRules = definition['metricRules']
    const remake = asRecord(definition['remake'], 'the remake treatment')
    const deduction = asRecord(remake['deduction'], 'the remake deduction')
    return {
      id: row.id,
      schemeId: row.schemeId,
      versionNumber: row.versionNumber,
      version: row.version,
      branchId: row.branchId,
      currency: row.currency,
      status: row.status,
      label: row.label,
      rounding: String(definition['rounding']),
      earnsOn: String(definition['earnsOn']),
      attributionOrder: asList(definition['attributionOrder'], 'the credit order').map((role) =>
        String(role),
      ),
      rules: toSchemeRules(definition['rules']),
      // Null is "no threshold rule", which the column allows and which is a different fact from
      // a threshold rule with no steps.
      ...(tier === undefined || tier === null ? {} : { tier: toSchemeTier(tier) }),
      ...(metricRules === undefined || metricRules === null
        ? {}
        : { metricRules: asList(metricRules, 'the behaviour bonuses') }),
      remake: {
        reducingCauses: asList(remake['reducingCauses'], 'the remake causes').map((cause) =>
          String(cause),
        ),
        deductionKind: String(deduction['kind']),
        ...(deduction['amountMinor'] === undefined
          ? {}
          : { deductionAmountMinor: String(deduction['amountMinor']) }),
      },
      ...(optionalText(row.publishedAt) === undefined
        ? {}
        : { publishedAt: String(row.publishedAt) }),
      ...(optionalText(row.withdrawnAt) === undefined
        ? {}
        : { withdrawnAt: String(row.withdrawnAt) }),
      ...(optionalText(row.withdrawReason) === undefined
        ? {}
        : { withdrawReason: String(row.withdrawReason) }),
    }
  })
}

/**
 * `GET /loan-frames/overdue` and `GET /loan-frames/:loanFrameId`. §6.27 `REP-005`; §15.13.
 *
 * **The money is split here, as everywhere else.** The server sends `deposit`,
 * `writeOff.retainedDeposit` and `writeOff.returnedDeposit` as `{minor, currency}` and the screen
 * wants minor units beside one currency, which is the fourth-instance mismatch `readOrders`
 * describes. `need()` is what turns a server that stops sending the field into a named message
 * rather than `parseMinorUnits(undefined)` unmounting the list.
 *
 * **`chases` goes through `listOf`.** The wire type says it is always an array, and so did four
 * other reads before one of them was not.
 *
 * **`overdue` is the server's own comparison** against its clock, never this device's, for the
 * reason `readRecalls` gives about `lapsed`: a shop with a wrong clock would chase a customer whose
 * frame is not late. The overdue endpoint has already filtered on it, so on that list it is always
 * true; on a single loan read by id it is the only thing that says whether this one is late.
 *
 * There is **no cost or margin field on this record**, so `ROLE-001a` does not arise here: a
 * deposit is a customer's money rather than the shop's buying price, and it is sent to every reader
 * who holds `loan_frame_issue`. Worth knowing before somebody adds a frame valuation to the view.
 */
interface WireLoanFrameChase {
  readonly attemptedAt: string
  readonly attemptedByStaffId: string
  readonly channel: string
  readonly note?: string
}

interface WireLoanFrame {
  readonly id: string
  readonly branchId: string
  readonly customerId: string
  readonly repairId?: string
  readonly remakeId?: string
  readonly stockItemId: string
  readonly frameVariantId: string
  readonly frame?: {
    readonly brand: string
    readonly model: string
    readonly collection?: string
    readonly colour?: string
    readonly size?: string
  }
  readonly deposit: WireMoney
  readonly issuedAt: string
  readonly issuedByStaffId: string
  readonly expectedReturnAt: string
  readonly returnedAt?: string
  readonly returnedConditionNote?: string
  readonly writeOff?: {
    readonly writtenOffAt: string
    readonly writtenOffByStaffId: string
    readonly reason: string
    readonly retainedDeposit: WireMoney
    readonly returnedDeposit: WireMoney
  }
  readonly chases: readonly WireLoanFrameChase[]
  readonly overdue: boolean
  readonly version: number
}

/** Shared, because both loan-frame reads answer with the same `LoanFrameView`. */
export const toLoanFrameData = (wire: WireLoanFrame): LoanFrameData => ({
  id: wire.id,
  branchId: wire.branchId,
  customerId: wire.customerId,
  ...(wire.repairId === undefined ? {} : { repairId: wire.repairId }),
  ...(wire.remakeId === undefined ? {} : { remakeId: wire.remakeId }),
  stockItemId: wire.stockItemId,
  frameVariantId: wire.frameVariantId,
  ...(wire.frame === undefined ? {} : { frame: wire.frame }),
  depositMinor: need(wire.deposit, 'deposit').minor,
  currency: wire.deposit.currency,
  issuedAt: wire.issuedAt,
  expectedReturnAt: wire.expectedReturnAt,
  ...(wire.returnedAt === undefined ? {} : { returnedAt: wire.returnedAt }),
  ...(wire.returnedConditionNote === undefined
    ? {}
    : { returnedConditionNote: wire.returnedConditionNote }),
  ...(wire.writeOff === undefined
    ? {}
    : {
        writeOff: {
          writtenOffAt: wire.writeOff.writtenOffAt,
          writtenOffByStaffId: wire.writeOff.writtenOffByStaffId,
          reason: wire.writeOff.reason,
          retainedMinor: need(wire.writeOff.retainedDeposit, 'writeOff.retainedDeposit').minor,
          returnedMinor: need(wire.writeOff.returnedDeposit, 'writeOff.returnedDeposit').minor,
        },
      }),
  chases: listOf(wire.chases, 'chase attempts').map((chase) => ({
    attemptedAt: chase.attemptedAt,
    attemptedByStaffId: chase.attemptedByStaffId,
    channel: chase.channel,
    ...(chase.note === undefined ? {} : { note: chase.note }),
  })),
  overdue: wire.overdue,
  version: wire.version,
})

export const readOverdueLoanFrames = async (
  config: ReadsConfig,
  branchId: string,
): Promise<readonly LoanFrameData[]> => {
  const body = await request<{ readonly loanFrames: readonly WireLoanFrame[] }>(
    config,
    `/loan-frames/overdue?${new URLSearchParams({ branchId }).toString()}`,
  )
  return listOf(body?.loanFrames, 'loan frames').map(toLoanFrameData)
}

/**
 * One loan by id, which is how a loan the overdue list cannot hold is read at all: the list filters
 * to overdue, so a frame lent out this morning is in no collection the counter can ask for.
 */
export const readLoanFrame = async (
  config: ReadsConfig,
  loanFrameId: string,
): Promise<LoanFrameData> =>
  toLoanFrameData(await request<WireLoanFrame>(config, `/loan-frames/${loanFrameId}`))

/** `OPS-204`'s six items, as `GET /onboarding` answers them. */
export interface OnboardingItem {
  readonly key: string
  readonly position: number
  readonly done: boolean
  readonly doneAt?: string
}

export interface OnboardingView {
  readonly items: readonly OnboardingItem[]
  readonly done: number
  readonly total: number
}

interface WireOnboarding {
  readonly items?: readonly {
    readonly key?: unknown
    readonly position?: unknown
    readonly done?: unknown
    readonly doneAt?: unknown
  }[]
  readonly done?: unknown
  readonly total?: unknown
}

/**
 * `GET /onboarding`. `OPS-204`: "In-product onboarding checklist with visible progress."
 *
 * **The rows existed for a long time before anything read them.** `SignupService.activate` has
 * seeded six per shop since the billing slice, and no route, screen or service ever looked, so a
 * shop that signed up got the empty product and nothing telling it where to start.
 *
 * Mapped rather than cast, like every other read here: a field the server stops sending becomes a
 * missing item rather than a white screen.
 */
export const readOnboarding = async (config: ReadsConfig): Promise<OnboardingView> => {
  const body = await request<WireOnboarding>(config, '/onboarding')
  const items = listOf(body?.items, 'items')
    .map((row) => ({
      key: String(row?.key ?? ''),
      position: Number(row?.position ?? 0),
      done: row?.done === true,
      ...(typeof row?.doneAt === 'string' ? { doneAt: row.doneAt } : {}),
    }))
    .filter((row) => row.key !== '')
  return {
    items,
    done: items.filter((row) => row.done).length,
    total: items.length,
  }
}

/** `OPS-207`: whether to ask, and the bounds the form enforces so the server has less to refuse. */
export interface SatisfactionDueView {
  readonly due: boolean
  readonly min: number
  readonly max: number
  readonly commentMaxLength: number
}

interface WireSatisfactionDue {
  readonly due?: unknown
  readonly scale?: { readonly min?: unknown; readonly max?: unknown }
  readonly commentMaxLength?: unknown
}

/**
 * `GET /satisfaction/due`. `OPS-207`: "Periodic satisfaction measurement."
 *
 * Mapped rather than cast. Anything the server did not say becomes "not due": a prompt that cannot
 * tell whether to appear stays away, because asking a shop a question on a guess is worse than
 * asking it a week late.
 */
export const readSatisfactionDue = async (config: ReadsConfig): Promise<SatisfactionDueView> => {
  const body = await request<WireSatisfactionDue>(config, '/satisfaction/due')
  const min = Number(body?.scale?.min)
  const max = Number(body?.scale?.max)
  const commentMaxLength = Number(body?.commentMaxLength)
  const usable =
    body?.due === true &&
    Number.isInteger(min) &&
    Number.isInteger(max) &&
    min < max &&
    Number.isInteger(commentMaxLength) &&
    commentMaxLength > 0
  return usable
    ? { due: true, min, max, commentMaxLength }
    : { due: false, min: 0, max: 0, commentMaxLength: 0 }
}

/** `ADM-005`: one consent the shop has given, in the state the server derived from the clock. */
export interface SupportAccessConsent {
  readonly consentId: string
  readonly state: 'live' | 'expired' | 'revoked'
  readonly grantedAt: string
  readonly expiresAt: string
  readonly scope: readonly string[]
}

/** `ROLE-005`: a time an operator used, or was refused, the shop's consent. */
export interface SupportAccessUseRow {
  readonly at: string
  readonly operatorName: string
  readonly reason: string
  readonly allowed: boolean
}

export interface SupportAccessView {
  readonly consents: readonly SupportAccessConsent[]
  /** The gate's own ceiling, so the form refuses a long window before the server has to. */
  readonly maxDurationMinutes: number
  readonly uses: readonly SupportAccessUseRow[]
}

/**
 * `GET /support-access`. Mapped, never cast. A ceiling the server did not send becomes 0, which
 * the form reads as "cannot grant here", so a version mismatch cannot offer a window the server
 * would refuse and halt the write queue with.
 */
export const readSupportAccess = async (config: ReadsConfig): Promise<SupportAccessView> => {
  const body = await request<{
    consents?: unknown
    uses?: unknown
    maxDurationMinutes?: unknown
  }>(config, '/support-access')
  const max = Number(body.maxDurationMinutes)
  return {
    maxDurationMinutes: Number.isInteger(max) && max > 0 ? max : 0,
    consents: asList(body.consents, 'the support access grants').map((row) => {
      const wire = row as Record<string, unknown>
      const state = wire['state']
      return {
        consentId: String(wire['consentId']),
        state: state === 'live' || state === 'revoked' ? state : 'expired',
        grantedAt: String(wire['grantedAt']),
        expiresAt: String(wire['expiresAt']),
        scope: Array.isArray(wire['scope']) ? wire['scope'].map(String) : [],
      }
    }),
    uses: asList(body.uses, 'the support access uses').map((row) => {
      const wire = row as Record<string, unknown>
      return {
        at: String(wire['at']),
        operatorName: String(wire['operatorName']),
        reason: String(wire['reason']),
        allowed: wire['allowed'] === true,
      }
    }),
  }
}
/* -------------------------------------------------------------------------- *
 * The reads the seven screens of `decisions/0040`'s round take.
 *
 * Thirteen tables had an API that wrote them and no screen that could reach them, and seven screens
 * landed taking every read as a `load*` prop so they could be built at once. These are those reads.
 *
 * **Every one of them is a mapper and not one of them is a cast**, for the reason the block above
 * `wireText` gives: three screens crashed at the till on fields the server does not send, all three
 * typechecked, and `readSuppliers` then defaulted a `version` and would have stopped a shop taking
 * payment. So a field a screen sends back to the server is required here, and a field that is
 * genuinely optional is `null` rather than the four characters `undefined`.
 * -------------------------------------------------------------------------- */

/**
 * A boolean the mapper cannot do without.
 *
 * `wire['x'] === true` is right for a flag whose absence means off, which is why the mappers above
 * use it. It is wrong for a flag a screen hands straight back: `trades`, `isAvailable`,
 * `substitutionAllowed` and `isActive` each ride a `PATCH` body, so a server that stopped sending
 * one would have the counter writing `false` over a `true` nobody chose, silently and once per
 * edit.
 */
const wireBool = (wire: Record<string, unknown>, key: string, what: string): boolean => {
  const value = wire[key]
  if (value !== true && value !== false) {
    throw new ReadFailed(
      500,
      'READ_CONTRACT',
      `The server sent ${what} with no ${key}. This is a version mismatch, not your fault.`,
    )
  }
  return value
}

/** Absent and null are one thing here, and neither is an empty string. */
const absent = (value: unknown): boolean => value === undefined || value === null

/**
 * A number that is allowed to be absent, and is never allowed to be rubbish.
 *
 * `Number(undefined)` is `NaN` and `Number('')` is `0`, and a form field filled from either reads
 * as a figure somebody chose. Absent stays absent; unreadable is a refusal.
 */
const wireNumberOrNull = (
  wire: Record<string, unknown>,
  key: string,
  what: string,
): number | null => {
  const value = wire[key]
  if (absent(value)) return null
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) {
    throw new ReadFailed(
      500,
      'READ_CONTRACT',
      `The server sent ${what} with an unreadable ${key}. This is a version mismatch, not your fault.`,
    )
  }
  return parsed
}

/** Text that is allowed to be absent. Never `String(undefined)`: see `wireText`. */
const wireTextOrNull = (wire: Record<string, unknown>, key: string): string | null =>
  absent(wire[key]) ? null : String(wire[key])

/** A list of text with every element checked, so one bad element is a message and not a blank row. */
const textList = (value: unknown, what: string): readonly string[] =>
  asList(value, what).map((item, index) => {
    if (typeof item !== 'string' || item === '') {
      throw new ReadFailed(
        500,
        'READ_CONTRACT',
        `The server sent ${what} with nothing at position ${index + 1}. This is a version mismatch, not your fault.`,
      )
    }
    return item
  })

/** The same for numbers. An axis of `NaN` renders as a blank degree and is sent back as one. */
const numberList = (value: unknown, what: string): readonly number[] =>
  asList(value, what).map((item, index) => {
    const parsed = Number(item)
    if (absent(item) || !Number.isFinite(parsed)) {
      throw new ReadFailed(
        500,
        'READ_CONTRACT',
        `The server sent ${what} with no number at position ${index + 1}. This is a version mismatch, not your fault.`,
      )
    }
    return parsed
  })

/**
 * `GET /branch-groups`. §17 `BR-02`, `BR-12`; `tenant_settings_policies`, the owner alone.
 *
 * Which branches share a customer. `shared_group` could be chosen and never satisfied until the
 * route existed, and until this read a shop still could not see a group it had made.
 */
export const readBranchGroups = async (config: ReadsConfig): Promise<readonly BranchGroupRow[]> => {
  const body = await request<{ groups?: unknown }>(config, '/branch-groups')
  const what = 'a branch group'
  return asList(body.groups, 'the branch groups').map((row) => {
    const wire = asRecord(row, what)
    return {
      id: wireText(wire, 'id', what),
      name: wireText(wire, 'name', what),
      // The `PUT` replaces membership wholesale, so a group read with a member missing is a group
      // saved with that member dropped. One unreadable id is a refusal, not a shorter list.
      branchIds: textList(wire['branchIds'], 'the branches in a group'),
      version: wireNumber(wire, 'version', what),
    }
  })
}

/**
 * `GET /recall-rules`. §6.24 `APT-004`; `tenant_settings_policies`.
 *
 * **An object, not a bare array.** `RecallRulesController.list` answers `{ rules }`, so this
 * unwraps rather than mapping the body, which is the shape `GET /promotions` does not have and the
 * one a cast would have got wrong in both directions.
 *
 * All four kinds always come back, configured or not, which is what lets the screen show a kind
 * nobody has set up yet instead of an empty list.
 */
export const readRecallRules = async (config: ReadsConfig): Promise<readonly RecallRuleData[]> => {
  const body = await request<{ rules?: unknown }>(config, '/recall-rules')
  const what = 'a recall rule'
  return asList(body.rules, 'the recall rules').map((row) => {
    const wire = asRecord(row, what)
    const configured = wireBool(wire, 'configured', what)
    const days = wire['daysPerUnit']
    return {
      kind: wireText(wire, 'kind', what),
      configured,
      isActive: wireBool(wire, 'isActive', what),
      intervalMonths: wireNumberOrNull(wire, 'intervalMonths', what),
      leadDays: wireNumberOrNull(wire, 'leadDays', what),
      reorderLeadDays: wireNumberOrNull(wire, 'reorderLeadDays', what),
      daysPerUnit: absent(days)
        ? null
        : Object.fromEntries(
            Object.entries(asRecord(days, 'how long a lens lasts')).map(([modality, value]) => {
              const parsed = Number(value)
              if (absent(value) || !Number.isFinite(parsed)) {
                throw new ReadFailed(
                  500,
                  'READ_CONTRACT',
                  `The server sent no number of days for ${modality}. This is a version mismatch, not your fault.`,
                )
              }
              return [modality, parsed]
            }),
          ),
      repeatEveryDays: wireNumberOrNull(wire, 'repeatEveryDays', what),
      maxNudges: wireNumberOrNull(wire, 'maxNudges', what),
      /*
       * **Null exactly when the rule does not exist yet**, which is what `configured` says, and
       * never a default. `RecallRules.tsx` omits `expectedVersion` when this is null, so a
       * configured rule read with no version would produce a write with no lock against a row that
       * has one: a 409, at the head of the queue, which stops the till.
       */
      version: configured ? wireNumber(wire, 'version', what) : null,
    }
  })
}

/**
 * `GET /branches/:branchId/appointment-closures`. §6.14 `APT-002`; `tenant_settings_policies`.
 *
 * The days the branch is shut. Every row ever, oldest first: the screen splits them at the
 * branch's today itself, because there is no from-date on the route.
 */
export const readAppointmentClosures = async (
  config: ReadsConfig,
  branchId: string,
): Promise<readonly ClosureRow[]> => {
  const body = await request<{ closures?: unknown }>(
    config,
    `/branches/${encodeURIComponent(branchId)}/appointment-closures`,
  )
  const what = 'a day off'
  return asList(body.closures, 'the days off').map((row) => {
    const wire = asRecord(row, what)
    return {
      id: wireText(wire, 'id', what),
      branchId: wireText(wire, 'branchId', what),
      onDate: wireText(wire, 'onDate', what),
      // Required, not `=== true`: the edit that opens a day again sends this back, so a default
      // would trade on a country holiday the shop had said it would not.
      trades: wireBool(wire, 'trades', what),
      label: wireTextOrNull(wire, 'label'),
      version: wireNumber(wire, 'version', what),
    }
  })
}

/**
 * `GET /branches/:branchId/appointment-hours-overrides`. §6.14 `APT-002`.
 *
 * Seasonal hours. Archived rows come back too, ordered archived-last, which is why the screen
 * sorts again: a stopped season is restored rather than recreated.
 */
export const readAppointmentHoursOverrides = async (
  config: ReadsConfig,
  branchId: string,
): Promise<readonly OverrideRow[]> => {
  const body = await request<{ overrides?: unknown }>(
    config,
    `/branches/${encodeURIComponent(branchId)}/appointment-hours-overrides`,
  )
  const what = 'a season of hours'
  return asList(body.overrides, 'the seasonal hours').map((row) => {
    const wire = asRecord(row, what)
    return {
      id: wireText(wire, 'id', what),
      branchId: wireText(wire, 'branchId', what),
      fromDate: wireText(wire, 'fromDate', what),
      toDate: wireText(wire, 'toDate', what),
      // Required, and 0 is a real opening minute, which is why `wireNumber` is the helper and not
      // a truthiness check.
      opensAtMinute: wireNumber(wire, 'opensAtMinute', what),
      closesAtMinute: wireNumber(wire, 'closesAtMinute', what),
      archivedAt: wireTextOrNull(wire, 'archivedAt'),
      version: wireNumber(wire, 'version', what),
    }
  })
}

/**
 * `GET /expenses?branchId=`. §6.27 `BR-11`; `expenses_record`.
 *
 * What the shop has spent, which until this release nothing could record: `allocateExpense` and
 * `postExpense` were built, pure and tested, and had no caller, so the table was named by no file
 * in `apps/`, `services/` or `tools/` at all.
 *
 * **The amount stays a digit string**, like a supplier cost and for the same reason: JSON has no
 * bigint and `formatWireForDisplay` throwing inside render is a white screen where this layer owes
 * a sentence. `parseMinorUnits` is used to refuse a bad one here, not to turn it into a number.
 *
 * `branchId` is null for a shared cost, which is a real answer and not a missing field: `BR-11`
 * apportions a chain-level cost across branches, so the row that holds it belongs to no branch.
 */
export const readExpenses = async (
  config: ReadsConfig,
  branchId: string,
): Promise<readonly ExpenseView[]> => {
  const body = await request<{ expenses?: unknown }>(
    config,
    `/expenses?branchId=${encodeURIComponent(branchId)}`,
  )
  const what = 'an expense'
  return asList(body.expenses, 'the expenses').map((row) => {
    const wire = asRecord(row, what)
    const amount = asRecord(wire['amount'], "an expense's amount")
    const minor = wireText(amount, 'minor', "an expense's amount")
    try {
      parseMinorUnits(minor)
    } catch {
      throw new ReadFailed(
        500,
        'READ_CONTRACT',
        'The server sent an expense whose amount is not a number of minor units. This is a version mismatch, not your fault.',
      )
    }
    return {
      id: wireText(wire, 'id', what),
      category: wireText(wire, 'category', what) as ExpenseView['category'],
      allocation: wireText(wire, 'allocation', what) as ExpenseView['allocation'],
      branchId: wireTextOrNull(wire, 'branchId'),
      amount: { minor, currency: wireText(amount, 'currency', "an expense's amount") },
      paidFrom: wireText(wire, 'paidFrom', what) as ExpenseView['paidFrom'],
      incurredOn: wireText(wire, 'incurredOn', what),
      note: wireTextOrNull(wire, 'note'),
    }
  })
}

/**
 * `GET /settings/policies`. `POS-008a`, `POS-006`, `CAT-015`; `tenant_settings_policies`.
 *
 * The three tenant policy numbers nothing could set. **Every one is nullable and null means no
 * rule**, which is `POS-008a`'s own wording: "Until a tenant sets one the rule is inert". So a
 * missing field is read as null and a present one must be a whole number, and neither is defaulted
 * to a figure nobody chose.
 *
 * **Throws on a failed read rather than answering an empty row**, because the row carries the
 * `version` a save goes back with. An invented version is a conflict at the head of the write
 * queue; an invented null is a limit silently removed from every role.
 *
 * `discountLimitByRole` is role to percentage string, and a role absent from it has no limit. The
 * percentages stay strings: they are compared against a discount by the server, and turning
 * "7.50" into 7.5 here would start a rounding argument this layer has no business in.
 */
export const readTenantPolicies = async (config: ReadsConfig): Promise<PoliciesRow> => {
  const what = 'the shop policies'
  const wire = asRecord(await request<unknown>(config, '/settings/policies'), what)
  const whole = (key: string): number | null => {
    const value = wire[key]
    if (value === null || value === undefined) return null
    if (typeof value !== 'number' || !Number.isInteger(value)) {
      throw new ReadFailed(
        500,
        'READ_CONTRACT',
        `The server sent a ${what} value that is not a whole number. This is a version mismatch, not your fault.`,
      )
    }
    return value
  }
  const stored = wire['discountLimitByRole']
  let limits: Record<string, string> | null = null
  if (stored !== null && stored !== undefined) {
    const table = asRecord(stored, 'the discount limits')
    limits = {}
    for (const [role, value] of Object.entries(table)) {
      if (typeof value !== 'string') {
        throw new ReadFailed(
          500,
          'READ_CONTRACT',
          'The server sent a discount limit that is not a percentage. This is a version mismatch, not your fault.',
        )
      }
      limits[role] = value
    }
  }
  return {
    discountLimitByRole: limits as PoliciesRow['discountLimitByRole'],
    returnWindowDays: whole('returnWindowDays'),
    cutoutEdgingAllowanceTenthsMm: whole('cutoutEdgingAllowanceTenthsMm'),
    version: wireNumber(wire, 'version', what),
  }
}

/**
 * `GET /branches/:branchId/appointment-schedule`. §6.14 `APT-001`; `tenant_settings_policies`.
 *
 * The branch's base weekly hours, which every one of the other appointment reads is an exception
 * to: `isWorkingDay` reads this row before any closure or season, so a branch without one refuses
 * every booking and every slot read with `APPOINTMENT_SCHEDULE_MISSING`.
 *
 * **`null` is an answer and not a failure.** A branch with no row has had no hours chosen, which is
 * the ordinary state of a new shop, and `AppointmentHours` draws an empty form over a sentence
 * saying so rather than showing a 404.
 *
 * **A missing `schedule` key is a different thing and is refused.** Reading absent as null would
 * make a version mismatch read as "this branch is not set up", and the save that followed would
 * omit `expectedVersion` against a row that has one: `EXPECTED_VERSION_REQUIRED`, at the head of
 * the queue, which stops the till.
 *
 * `numberList` for the weekdays rather than a cast: 0 is Sunday and a `NaN` day would be sent back
 * as one. `wireNumber` for every minute field, because 0 is a real opening minute and a real
 * buffer, so no truthiness check can stand in for it.
 */
export const readAppointmentSchedule = async (
  config: ReadsConfig,
  branchId: string,
): Promise<ScheduleRow | null> => {
  const what = 'the appointment hours'
  const body = asRecord(
    await request<unknown>(
      config,
      `/branches/${encodeURIComponent(branchId)}/appointment-schedule`,
    ),
    what,
  )
  if (!('schedule' in body)) {
    throw new ReadFailed(
      500,
      'READ_CONTRACT',
      `The server sent no answer for ${what}. This is a version mismatch, not your fault.`,
    )
  }
  if (body['schedule'] === null) return null
  const wire = asRecord(body['schedule'], what)
  return {
    branchId: wireText(wire, 'branchId', what),
    openWeekdays: numberList(wire['openWeekdays'], 'the days a branch opens'),
    opensAtMinute: wireNumber(wire, 'opensAtMinute', what),
    closesAtMinute: wireNumber(wire, 'closesAtMinute', what),
    slotStepMinutes: wireNumber(wire, 'slotStepMinutes', what),
    bufferMinutes: wireNumber(wire, 'bufferMinutes', what),
    minNoticeMinutes: wireNumber(wire, 'minNoticeMinutes', what),
    maxAdvanceDays: wireNumber(wire, 'maxAdvanceDays', what),
    version: wireNumber(wire, 'version', what),
  }
}

/**
 * `GET /appointment-types`. §6.14 `APT-001`; `tenant_settings_policies`, the owner alone.
 *
 * What the shop offers, shop-wide rather than per branch. Archived types are returned flagged,
 * because an appointment booked last month still names the type it was booked for.
 */
export const readAppointmentTypes = async (
  config: ReadsConfig,
): Promise<readonly AppointmentTypeRow[]> => {
  const body = await request<{ types?: unknown }>(config, '/appointment-types')
  const what = 'an appointment type'
  return asList(body.types, 'the appointment types').map((row) => {
    const wire = asRecord(row, what)
    return {
      id: wireText(wire, 'id', what),
      name: wireText(wire, 'name', what),
      durationMinutes: wireNumber(wire, 'durationMinutes', what),
      // Not defaulted to `[]`. An empty list is a real answer (a frame adjustment needs nothing)
      // and a missing one would offer a booking the server refuses with `RESOURCE_KIND_MISSING`.
      requiredResourceKinds: textList(
        wire['requiredResourceKinds'],
        'what an appointment type needs',
      ),
      archivedAt: wireTextOrNull(wire, 'archivedAt'),
      version: wireNumber(wire, 'version', what),
    }
  })
}

/**
 * `GET /branches/:branchId/appointment-resources`. §6.14 `APT-001`.
 *
 * The branch's rooms, staff calendars and visiting optometrists. `staffId` is null for a room, and
 * stays null rather than becoming the word `undefined`, because the rename sends it back.
 */
export const readAppointmentResources = async (
  config: ReadsConfig,
  branchId: string,
): Promise<readonly AppointmentResourceRow[]> => {
  const body = await request<{ resources?: unknown }>(
    config,
    `/branches/${encodeURIComponent(branchId)}/appointment-resources`,
  )
  const what = 'a room or a person'
  return asList(body.resources, 'the rooms and people').map((row) => {
    const wire = asRecord(row, what)
    return {
      id: wireText(wire, 'id', what),
      branchId: wireText(wire, 'branchId', what),
      kind: wireText(wire, 'kind', what),
      name: wireText(wire, 'name', what),
      staffId: wireTextOrNull(wire, 'staffId'),
      archivedAt: wireTextOrNull(wire, 'archivedAt'),
      version: wireNumber(wire, 'version', what),
    }
  })
}

/**
 * `GET /appointment-resources/:resourceId/visit-dates`. §6.14; `appointment_resource_visit`.
 *
 * The days a visiting optometrist is in, as a flat `{ resourceId, dates, version }` with no
 * wrapper, and `version` is the **resource's**, which the set bumps.
 *
 * **`dates` is required and is deliberately not defaulted to `[]`.** The `PUT` replaces the whole
 * set, so a read that invented an empty month followed by one Save is a month of visits deleted
 * with nobody having asked for it. A missing list is a refusal the owner can retry.
 */
export const readVisitDates = async (
  config: ReadsConfig,
  resourceId: string,
): Promise<VisitDatesRow> => {
  const what = 'the visiting days'
  const wire = asRecord(
    await request<unknown>(
      config,
      `/appointment-resources/${encodeURIComponent(resourceId)}/visit-dates`,
    ),
    what,
  )
  return {
    resourceId: wireText(wire, 'resourceId', what),
    dates: textList(wire['dates'], what),
    version: wireNumber(wire, 'version', what),
  }
}

/**
 * `GET /suppliers/:supplierId/prices`. §6.19 `SUP-002`; `cost_price_view` plus `ROLE-001a`'s
 * per-branch cost grant, which the service checks and the role row alone does not give.
 *
 * **The cost stays a digit string.** JSON has no bigint and a cost can exceed
 * `Number.MAX_SAFE_INTEGER`, so `parseMinorUnits` is used to refuse a cost that is not one rather
 * than to turn it into a number. Without that check `formatWireForDisplay` throws inside render,
 * which is a white screen where this layer owes a sentence.
 *
 * The 403 this can answer is turned into `PriceListDenied` by the loader in `App.tsx`, not here:
 * that class belongs to the screen and this file imports no runtime value from one.
 */
export const readSupplierPrices = async (
  config: ReadsConfig,
  supplierId: string,
): Promise<readonly SupplierPriceData[]> => {
  const body = await request<{ prices?: unknown }>(
    config,
    `/suppliers/${encodeURIComponent(supplierId)}/prices`,
  )
  const what = 'a supplier price'
  return asList(body.prices, "a supplier's prices").map((row) => {
    const wire = asRecord(row, what)
    const cost = asRecord(wire['unitCost'], "a supplier price's cost")
    const minor = wireText(cost, 'minor', "a supplier price's cost")
    try {
      parseMinorUnits(minor)
    } catch {
      throw new ReadFailed(
        500,
        'READ_CONTRACT',
        'The server sent a supplier price whose cost is not a number of minor units. This is a version mismatch, not your fault.',
      )
    }
    return {
      id: wireText(wire, 'id', what),
      sku: wireText(wire, 'sku', what),
      description: wireText(wire, 'description', what),
      unitCostMinor: minor,
      // The supplier's own currency, which is the only currency a price can have: the screen shows
      // it as a hint and never as a field, so nothing implies it can be changed here.
      currency: wireText(cost, 'currency', "a supplier price's cost"),
      isAvailable: wireBool(wire, 'isAvailable', what),
      leadTimeDays: wireNumberOrNull(wire, 'leadTimeDays', what),
      validFrom: wireText(wire, 'validFrom', what),
      validTo: wireTextOrNull(wire, 'validTo'),
      version: wireNumber(wire, 'version', what),
    }
  })
}

/**
 * `GET /catalogue/contact-lens-products`. §6.4 `CAT-020` to `CAT-022`; `catalogue_view`.
 *
 * **The flat fields, and `grid` is ignored.** The server sends both: the flat columns it stores and
 * a `grid` that collects them, and the screen edits the columns. Mapping both would be two sources
 * for one answer, and the one a form writes back has to be the stored one.
 *
 * `truncated` is the server's own "more than the limit matched". There is no next page, so the
 * screen says so rather than paging.
 */
export const readClProducts = async (
  config: ReadsConfig,
  query: { readonly q: string; readonly includeDiscontinued: boolean },
): Promise<ClProductList> => {
  const params = new URLSearchParams()
  if (query.q !== '') params.set('q', query.q)
  params.set('includeDiscontinued', query.includeDiscontinued ? 'true' : 'false')
  const listWhat = 'the contact lens catalogue'
  const body = asRecord(
    await request<unknown>(config, `/catalogue/contact-lens-products?${params.toString()}`),
    listWhat,
  )
  const what = 'a contact lens'
  return {
    truncated: wireBool(body, 'truncated', listWhat),
    products: asList(body['products'], 'the contact lenses on file').map((row) => {
      const wire = asRecord(row, what)
      const curves = wire['baseCurvesMm']
      const diameters = wire['diametersMm']
      const axes = wire['axesDegrees']
      return {
        clProductId: wireText(wire, 'clProductId', what),
        brand: wireText(wire, 'brand', what),
        name: wireText(wire, 'name', what),
        manufacturer: wireTextOrNull(wire, 'manufacturer'),
        udi: wireTextOrNull(wire, 'udi'),
        modality: wireText(wire, 'modality', what),
        material: wireTextOrNull(wire, 'material'),
        // A string, as the column holds it: a water content of 42.50 is not a float here either.
        waterContentPercent: wireTextOrNull(wire, 'waterContentPercent'),
        unitsPerBox: wireNumber(wire, 'unitsPerBox', what),
        baseCurvesMm: absent(curves) ? null : textList(curves, 'the base curves of a contact lens'),
        diametersMm: absent(diameters)
          ? null
          : textList(diameters, 'the diameters of a contact lens'),
        sphereMin: wireNumberOrNull(wire, 'sphereMin', what),
        sphereMax: wireNumberOrNull(wire, 'sphereMax', what),
        cylinderMin: wireNumberOrNull(wire, 'cylinderMin', what),
        cylinderMax: wireNumberOrNull(wire, 'cylinderMax', what),
        axesDegrees: absent(axes) ? null : numberList(axes, 'the axes of a contact lens'),
        addMin: wireNumberOrNull(wire, 'addMin', what),
        addMax: wireNumberOrNull(wire, 'addMax', what),
        substitutionAllowed: wireBool(wire, 'substitutionAllowed', what),
        discontinuedAt: wireTextOrNull(wire, 'discontinuedAt'),
        version: wireNumber(wire, 'version', what),
      }
    }),
  }
}

/**
 * `GET /branches/:branchId/appointments?date=`. §6.14 `APT-001`; `customer_view`.
 *
 * One branch's day, in time order. `resourceId` narrows it to one room or one person, and is sent
 * only when there is one: an empty one reads the same to the controller, and a query string that
 * grows a field nobody meant is how the next shape bug gets written.
 *
 * **A bare array**, unlike every other read in this round. `BookingController.listDay` returns the
 * service's own list rather than wrapping it, so this maps the body itself.
 */
export const readBranchDay = async (
  config: ReadsConfig,
  branchId: string,
  onDate: string,
  resourceId?: string,
): Promise<readonly AppointmentRow[]> => {
  const params = new URLSearchParams()
  params.set('date', onDate)
  if (resourceId !== undefined && resourceId !== '') params.set('resourceId', resourceId)
  const body = await request<unknown>(
    config,
    `/branches/${encodeURIComponent(branchId)}/appointments?${params.toString()}`,
  )
  const what = 'an appointment'
  const roomWhat = 'a room on an appointment'
  return asList(body, "the day's appointments").map((row) => {
    const wire = asRecord(row, what)
    const notes = optionalText(wire['notes'])
    const from = optionalText(wire['rescheduledFromId'])
    const to = optionalText(wire['rescheduledToId'])
    return {
      id: wireText(wire, 'id', what),
      customerId: wireText(wire, 'customerId', what),
      /*
       * **Null is withheld and has to survive as null.** The column is `not null`, so a null name
       * means the customer was erased or is outside this reader's `BR-02` reach, and `Diary.tsx`
       * withholds Move on exactly that row because `reschedule` re-places the booking and `place`
       * answers 404. `String(undefined)` here would put the word `undefined` on the row and offer
       * a move that is a guaranteed blocked head.
       */
      customerName: wireTextOrNull(wire, 'customerName'),
      typeId: wireText(wire, 'typeId', what),
      typeName: wireText(wire, 'typeName', what),
      localDate: wireText(wire, 'localDate', what),
      startMinute: wireNumber(wire, 'startMinute', what),
      startsAt: wireText(wire, 'startsAt', what),
      endsAt: wireText(wire, 'endsAt', what),
      durationMinutes: wireNumber(wire, 'durationMinutes', what),
      // Unnarrowed: `ARC-020` keeps a status this build has not met readable rather than blank.
      status: wireText(wire, 'status', what),
      resources: asList(wire['resources'], 'the rooms an appointment is in').map((entry) => {
        const resource = asRecord(entry, roomWhat)
        return {
          id: wireText(resource, 'id', roomWhat),
          name: wireText(resource, 'name', roomWhat),
        }
      }),
      resourceIds: textList(wire['resourceIds'], 'the rooms an appointment is in'),
      // Present or absent, never null: the screen tests `undefined`, and
      // `exactOptionalPropertyTypes` would not let a null through anyway.
      ...(notes === undefined ? {} : { notes }),
      ...(from === undefined ? {} : { rescheduledFromId: from }),
      ...(to === undefined ? {} : { rescheduledToId: to }),
      // No default. `ARC-007`'s lock rides every change this screen makes.
      version: wireNumber(wire, 'version', what),
    }
  })
}

/**
 * `GET /branches/:branchId/bookable`. §6.14, `decisions/0039`; `customer_view`.
 *
 * What this branch can be booked for, both halves in one request because the form needs both at
 * once and a shop on a Pakistani uplink should not pay two round trips for one screen. Archived
 * rows are already left out server side, so nothing is filtered here.
 */
export const readBookable = async (
  config: ReadsConfig,
  branchId: string,
): Promise<BookableOptions> => {
  const body = asRecord(
    await request<unknown>(config, `/branches/${encodeURIComponent(branchId)}/bookable`),
    'what this branch books',
  )
  const typeWhat = 'an appointment type'
  const resourceWhat = 'a room or a person'
  return {
    types: asList(body['types'], 'the appointment types').map((row) => {
      const wire = asRecord(row, typeWhat)
      return {
        id: wireText(wire, 'id', typeWhat),
        name: wireText(wire, 'name', typeWhat),
        durationMinutes: wireNumber(wire, 'durationMinutes', typeWhat),
        requiredResourceKinds: textList(
          wire['requiredResourceKinds'],
          'what an appointment type needs',
        ),
      }
    }),
    resources: asList(body['resources'], 'the rooms and people').map((row) => {
      const wire = asRecord(row, resourceWhat)
      return {
        id: wireText(wire, 'id', resourceWhat),
        name: wireText(wire, 'name', resourceWhat),
        kind: wireText(wire, 'kind', resourceWhat),
      }
    }),
  }
}

/**
 * `GET /branches/:branchId/appointment-slots`. §6.14 `APT-001`; `customer_view`.
 *
 * The times a booking of this shape could start. Three things about the query string are load
 * bearing rather than stylistic:
 *
 * - **`resourceIds` is comma separated**, because a query string has no arrays and `?a=1&a=2`
 *   arrives as a string or an array depending on how many there are. An empty string is a real
 *   answer and means none, which a frame adjustment is.
 * - **`excludeAppointmentId` is sent only when the caller passes one**, which is the move form and
 *   never a new booking. From a new booking it offers a slot that `book` then refuses as double
 *   booked: a 409, at the head of the queue, with no server-side guard against it.
 * - **`reason` is `string | null` and is never dropped.** The screen words five situations from it,
 *   and a day with no free time and no reason is the empty array that four situations shared.
 */
export const readAppointmentSlots = async (
  config: ReadsConfig,
  branchId: string,
  query: SlotQuery,
): Promise<OpenSlotsData> => {
  const params = new URLSearchParams()
  params.set('date', query.date)
  params.set('typeId', query.typeId)
  params.set('resourceIds', query.resourceIds.join(','))
  if (query.excludeAppointmentId !== undefined && query.excludeAppointmentId !== '') {
    params.set('excludeAppointmentId', query.excludeAppointmentId)
  }
  const what = 'the free times'
  const body = asRecord(
    await request<unknown>(
      config,
      `/branches/${encodeURIComponent(branchId)}/appointment-slots?${params.toString()}`,
    ),
    what,
  )
  return {
    date: wireText(body, 'date', what),
    slots: asList(body['slots'], what).map((row) => {
      const wire = asRecord(row, 'a free time')
      return {
        startMinute: wireNumber(wire, 'startMinute', 'a free time'),
        startsAt: wireText(wire, 'startsAt', 'a free time'),
      }
    }),
    reason: wireTextOrNull(body, 'reason'),
  }
}

/**
 * `GET /panels`. The statement screen's chooser, section 6.13 `CLM-001`.
 *
 * Deactivated panels come back flagged rather than filtered, which is `readSuppliers`' reason: a
 * statement issued last year names a panel the shop has since stopped dealing with, and that
 * statement still has to be readable.
 */
export const readPanels = async (config: ReadsConfig): Promise<readonly PanelOption[]> => {
  const body = await request<{ panels?: unknown }>(config, '/panels')
  const what = 'a panel'
  return asList(body.panels, 'the panels').map((row) => {
    const wire = asRecord(row, what)
    return {
      id: wireText(wire, 'id', what),
      name: wireText(wire, 'name', what),
      currency: wireText(wire, 'currency', what),
      deactivated: wire['deactivated'] === true,
    }
  })
}

/**
 * `GET /panels/:panelId/statements`. Section 15.20 step 4, `CLM-001` to `CLM-006`.
 *
 * **Every money field is read, and a missing one throws rather than rendering as nothing.** A
 * statement is the document a payer is holding, and `statementReconciles` says
 * `panel_share = paid + outstanding + denied + transferred + written_off`. A field this layer
 * quietly defaulted to zero would show a column that adds up on screen and does not add up in the
 * table, which is the bug migration 9028 exists to make impossible. So the seven columns are
 * mapped by name and `parseMinorUnits` is what decides whether a figure is a figure.
 *
 * `offers` is passed through as sent, including the server's own refusal sentence for a month it
 * will not issue. The screen pre-checks nothing itself: a refused write sits `blocked` at the head
 * of the write queue and stops every sale behind it (`OFF-001a`, `OFF-005`), so the decision about
 * which months are offered belongs to the one place that can see every branch's claims.
 */
export const readPanelStatements = async (
  config: ReadsConfig,
  panelId: string,
): Promise<PanelStatementsData> => {
  const what = 'a panel statement'
  const wire = asRecord(
    await request<unknown>(config, `/panels/${encodeURIComponent(panelId)}/statements`),
    'the statements',
  )

  /** One money column. The currency travels with the figure, per the money invariant. */
  const money = (from: Record<string, unknown>, key: string, where: string): WireMoney => {
    const value = asRecord(from[key], `${where} ${key}`)
    const minor = wireText(value, 'minor', `${where} ${key}`)
    try {
      parseMinorUnits(minor)
    } catch {
      throw new ReadFailed(
        500,
        'READ_CONTRACT',
        'The server sent a statement figure that is not a number of minor units. This is a version mismatch, not your fault.',
      )
    }
    return { minor, currency: wireText(value, 'currency', `${where} ${key}`) }
  }

  const totals = (from: Record<string, unknown>, where: string): StatementTotalsData => {
    const block = asRecord(from['totals'], `${where} totals`)
    return {
      panelShare: money(block, 'panelShare', where),
      customerShare: money(block, 'customerShare', where),
      paid: money(block, 'paid', where),
      outstanding: money(block, 'outstanding', where),
      denied: money(block, 'denied', where),
      transferred: money(block, 'transferred', where),
      writtenOff: money(block, 'writtenOff', where),
    }
  }

  return {
    panelId: wireText(wire, 'panelId', what),
    panel: wireText(wire, 'panel', what),
    currency: wireText(wire, 'currency', what),
    withheld: wireNumber(wire, 'withheld', what),
    statements: asList(wire['statements'], 'the statements').map((row) => {
      const statement = asRecord(row, what)
      return {
        id: wireText(statement, 'id', what),
        periodFrom: wireText(statement, 'periodFrom', what),
        periodTo: wireText(statement, 'periodTo', what),
        revision: wireNumber(statement, 'revision', what),
        superseded: statement['superseded'] === true,
        issuedAt: wireText(statement, 'issuedAt', what),
        totals: totals(statement, what),
        lines: asList(statement['lines'], 'the statement lines').map((entry) => {
          const line = asRecord(entry, 'a statement line')
          const where = 'a statement line'
          return {
            claimId: wireText(line, 'claimId', where),
            orderId: wireText(line, 'orderId', where),
            capturedOn: wireText(line, 'capturedOn', where),
            claimStatus: wireText(line, 'claimStatus', where),
            panelShare: money(line, 'panelShare', where),
            customerShare: money(line, 'customerShare', where),
            paid: money(line, 'paid', where),
            outstanding: money(line, 'outstanding', where),
            denied: money(line, 'denied', where),
            transferred: money(line, 'transferred', where),
            writtenOff: money(line, 'writtenOff', where),
          }
        }),
      }
    }),
    offers: asList(wire['offers'], 'the months').map((row) => {
      const offer = asRecord(row, 'a month')
      const supersedes = wireTextOrNull(offer, 'supersedesStatementId')
      const code = wireTextOrNull(offer, 'code')
      const message = wireTextOrNull(offer, 'message')
      return {
        month: wireText(offer, 'month', 'a month'),
        claims: wireNumber(offer, 'claims', 'a month'),
        offered: offer['offered'] === true,
        revision: wireNumber(offer, 'revision', 'a month'),
        ...(supersedes === null ? {} : { supersedesStatementId: supersedes }),
        ...(code === null ? {} : { code }),
        ...(message === null ? {} : { message }),
      }
    }),
  }
}

/**
 * One money figure off the wire, with the currency that travels with it. Appendix D `MR-01`.
 *
 * `parseMinorUnits` is what decides whether a figure is a figure, so a receivable whose amount did
 * not arrive is a message rather than a blank cell in the middle of what a panel owes. The claims
 * reads below share it; `readPanelStatements` keeps its own, which names the column it failed on.
 */
const panelMoney = (value: unknown, where: string): WireMoney => {
  const wire = asRecord(value, where)
  const minor = wireText(wire, 'minor', where)
  try {
    parseMinorUnits(minor)
  } catch {
    throw new ReadFailed(
      500,
      'READ_CONTRACT',
      `The server sent ${where} with a figure that is not a number of minor units. This is a version mismatch, not your fault.`,
    )
  }
  return { minor, currency: wireText(wire, 'currency', where) }
}

/**
 * `GET /panels/:panelId`. One panel with its plans and what each plan pays. §6.13 `CLM-001` to
 * `CLM-003`.
 *
 * **An absent coverage rule stays absent, and defaulting it would be a lie about somebody's
 * commercial terms.** A panel's negotiated rate is treated as cost price under `ROLE-001a`'s
 * per-branch grant, so the route omits `rule` for a caller without it rather than sending zeros.
 * `PanelAccounts` renders the gap as "Rate not shown to you"; a `{ kind: 'excluded' }` default
 * here would render as "Nothing", which says the panel pays for none of that category.
 */
export const readPanel = async (config: ReadsConfig, panelId: string): Promise<PanelDetailData> => {
  const what = 'a panel'
  const body = await request<{ panel?: unknown }>(config, `/panels/${encodeURIComponent(panelId)}`)
  const wire = asRecord(body.panel, what)

  const coverage = (value: unknown): readonly CoverageRowData[] =>
    asList(value, 'the coverage').map((entry) => {
      const row = asRecord(entry, 'a coverage rule')
      const category = wireText(row, 'category', 'a coverage rule')
      // `absent`, not falsy: this is the withheld case, and the screen has a word for it.
      if (row['rule'] === undefined || row['rule'] === null) return { category }
      const rule = asRecord(row['rule'], 'a coverage rule')
      const amount = rule['amount']
      const basisPoints = wireNumberOrNull(rule, 'basisPoints', 'a coverage rule')
      const rounding = wireTextOrNull(rule, 'payerShareRounding')
      return {
        category,
        rule: {
          kind: wireText(rule, 'kind', 'a coverage rule'),
          ...(amount === undefined || amount === null
            ? {}
            : { amount: panelMoney(amount, 'a coverage amount') }),
          ...(basisPoints === null ? {} : { basisPoints }),
          ...(rounding === null ? {} : { payerShareRounding: rounding }),
        },
      }
    })

  return {
    id: wireText(wire, 'id', what),
    name: wireText(wire, 'name', what),
    kind: wireText(wire, 'kind', what),
    currency: wireText(wire, 'currency', what),
    plans: asList(wire['plans'], 'the plans').map((entry): PanelPlanData => {
      const plan = asRecord(entry, 'a plan')
      return {
        id: wireText(plan, 'id', 'a plan'),
        name: wireText(plan, 'name', 'a plan'),
        eligibilityPolicy: wireText(plan, 'eligibilityPolicy', 'a plan'),
        authorisationRequired: plan['authorisationRequired'] === true,
        deactivated: plan['deactivated'] === true,
        coverage: coverage(plan['coverage']),
      }
    }),
  }
}

/**
 * `GET /orders/:orderId/claim`. What may be claimed on this sale, and the claim if one exists.
 * §15.20 steps 1 to 3.
 *
 * The mapping lives in `./claim-capture.js` beside the shape it produces, so it can be tested
 * without a server. This keeps the request, which is the division the rest of this file uses.
 */
export const readClaimCapture = async (
  config: ReadsConfig,
  orderId: string,
): Promise<ClaimCaptureData> =>
  parseClaimCapture(await request<unknown>(config, `/orders/${encodeURIComponent(orderId)}/claim`))

/**
 * `GET /claims/aged`. `CLM-004`'s aged claims, over the branches the caller holds.
 *
 * **`asOf` is required and has no default on the route.** The controller reads a missing query
 * parameter as `''` and `asIsoDay` refuses that with a 422, so the day is passed rather than left
 * to the server: the branch's own today, which is the day the shop is having rather than the
 * server's UTC date.
 *
 * `ageFrom=captured` is this screen's choice between the two the route offers. A claim is aged
 * from the day the shop captured it, because that is the day the shop started waiting; aging from
 * submission would restart the clock every time a refusal was billed again.
 */
export const readAged = async (
  config: ReadsConfig,
  currency: string,
  asOf: string,
): Promise<AgedData> => {
  const what = 'the aged claims'
  const query = new URLSearchParams({ currency, asOf, ageFrom: 'captured' })
  const wire = asRecord(await request<unknown>(config, `/claims/aged?${query.toString()}`), what)

  const open = (from: Record<string, unknown>, where: string): WireMoney =>
    panelMoney(from['open'], `${where} open`)

  const unsubmitted = asRecord(wire['notYetSubmitted'], what)
  return {
    buckets: asList(wire['buckets'], 'the buckets').map((entry) => {
      const bucket = asRecord(entry, 'a bucket')
      return {
        label: wireText(bucket, 'label', 'a bucket'),
        claimCount: wireNumber(bucket, 'claimCount', 'a bucket'),
        open: open(bucket, 'a bucket'),
      }
    }),
    notYetSubmitted: {
      claimCount: wireNumber(unsubmitted, 'claimCount', what),
      open: open(unsubmitted, what),
    },
    rows: asList(wire['rows'], 'the open claims').map((entry) => {
      const row = asRecord(entry, 'an open claim')
      const ageDays = wireNumberOrNull(row, 'ageDays', 'an open claim')
      const bucket = wireNumberOrNull(row, 'bucket', 'an open claim')
      return {
        claimId: wireText(row, 'claimId', 'an open claim'),
        panelId: wireText(row, 'panelId', 'an open claim'),
        open: open(row, 'an open claim'),
        // Absent on a claim never submitted: there is no clock to age it from, and a zero would
        // read as "today", which puts it in the youngest bucket rather than in no bucket.
        ...(ageDays === null ? {} : { ageDays }),
        ...(bucket === null ? {} : { bucket }),
      }
    }),
  }
}
